/**
 * cart-drawer.js — Two-Panel Slide-Out Cart Drawer
 * Fable Home v2 Cart
 */

(function () {
  'use strict';

  /* ============================================================
     CONFIG — injected by cart-drawer.liquid
     ============================================================ */
  const cfg = window.cartDrawerCfg || {
    insuranceEnabled: false,
    insuranceVariantId: null,
    freeShippingThreshold: 25000,
    currency: 'CAD',
    currencySymbol: '$',
    isMember: false,
    customerName: '',
    membershipVariantId: 0,
    membershipPlanId: 0,
    domain: '',
    recommendationSectionId: '',
    recommendationProductIds: [],
  };

  /* ============================================================
     FREE GIFT (GWP)
     ----------------------------------------------------------------
     Auto-added when items subtotal (excluding the gift itself) hits
     `thresholdCents`. The discount rule on the product side gives
     100% off so this line lands at $0 regardless of which variant
     the shopper picks via the in-cart swatch row.

     Per-domain config map because Shopify product/variant IDs do NOT
     carry across the CA/UK/US multi-store split ([[multi-store-architecture]]).
     Gating by `shop.domain` matches the project-wide convention used
     in snippets/account-membership.liquid, pdp-crystal-swatches.liquid,
     site_tracking.liquid, homepage_hero-video.liquid — "fable.com" is
     Canada, "us.fable.com" is US, "uk.fable.com" is UK. Off-rollout
     stores resolve to `null` here and the entire gift pipeline (sync,
     swatch fetch, render) no-ops via `_isGiftEnabledStore`.

     `handle` is the same on all three stores ('ceramic-carafe'), so
     sections/cart-gift-swatches.liquid stays hardcoded to that handle.
     If a future store uses a different handle, parameterize via a
     `case shop.domain` block in that section.

     The discount rule (Fable Discounts, fable-plus-10-regular)
     applies the 100% off discount to this product for ALL customers at
     the $350 threshold — it was initially Fable+-gated for the early-
     access window, then opened up store-wide. `memberOnly` here mirrors
     that gate; flipping it back to `true` will limit auto-add to
     Fable+ again (theme side only — you must also re-tighten the rule in
     admin or non-members will still see the gift at $0). The discount
     rule must exist on EACH store admin separately (re-point IDs per
     store) or shoppers on that store won't get the discount.
     ============================================================ */
  const FREE_GIFT_BY_DOMAIN = {
    'fable.com':    { productId: 7070745067709, defaultVariantId: 41040487448765, excludedProductIds: [7510073573565, 7640508432573] },
    'us.fable.com': { productId: 6680363860034, defaultVariantId: 39613857923138, excludedProductIds: [6965986721858] },
    'uk.fable.com': { productId: 7752830484735, defaultVariantId: 43171310076159, excludedProductIds: [8211490767103] },
  };
  const FREE_GIFT_BASE = {
    handle:             'ceramic-carafe',
    thresholdCents:     35000,
    propertyFlag:       '_free_gift',
    memberOnly:         false,
    excludedProductIds: [],
  };
  const FREE_GIFT = FREE_GIFT_BY_DOMAIN[cfg.domain]
    ? { ...FREE_GIFT_BASE, ...FREE_GIFT_BY_DOMAIN[cfg.domain] }
    : null;

  /* ============================================================
     FORMAT MONEY
     ============================================================ */
  function formatMoney(cents) {
    const fmt = cfg.moneyFormat || window.theme?.moneyFormat || '${{amount}}';
    if (window.Shopify && window.Shopify.formatMoney) {
      return Shopify.formatMoney(cents, fmt);
    }
    if (window.slate && window.slate.Currency) {
      return slate.Currency.formatMoney(cents, fmt);
    }
    const dollars = (cents / 100).toFixed(2);
    return cfg.currencySymbol + dollars + ' ' + cfg.currency;
  }

  /* "$1,600.00 CAD" — used in ORDER SUMMARY rows per design spec.
     Deliberately avoids `shop.money_with_currency_format` because Fable's
     CAD store has that setting configured as `${{amount_no_decimals}} CAD`
     — which truncated prices to whole dollars in the summary while the
     CHECKOUT button (using raw cents) kept the real figure, so
     `Subtotal $240 CAD + Shipping $11 CAD ≠ CHECKOUT $250.49` and users
     read it as a bug. Instead we reuse `shop.money_format` (authoritative
     decimals) and append the ISO currency code ourselves. */
  function formatMoneyWithCurrency(cents) {
    return `${formatMoney(cents)} ${cfg.currency || ''}`.trim();
  }

  /* ============================================================
     DOM HELPERS
     ============================================================ */
  const qs  = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /* ============================================================
     SHIPPING PROTECTION ESTIMATE
     ----------------------------------------------------------------
     The Shopify-side Shipping Protection app charges 4.5% of the
     ORIGINAL (compare-at) line prices, not the discounted total.
     Using cart.total_price gave a too-low estimate so users saw a
     CHECKOUT button several dollars below what checkout actually
     billed (Fable+ 30% off Dinnerware: drawer $3.44 vs checkout
     $4.91). Mirrors the cart.js:681 algorithm so the drawer matches
     the cart page and the real charge.
     ============================================================ */
  const RUG_SWATCH_CENTS = 2500; // $25 per swatch — bundle discount ignored

  function estimateInsuranceFee(cart) {
    if (!cart || !cart.items) return 0;

    const storedByobFull = parseInt(localStorage.getItem('byob_full_price')) || 0;
    let byobCounted = false;

    const rugSwatchCount = cart.items.filter(i =>
      (i.properties && i.properties._rug_swatches_bundle) ||
      i.handle === 'rug-swatches-bundle'
    ).length;

    const originalTotal = cart.items
      .filter(i => i.handle !== 'shipping-insurance')
      .reduce((sum, i) => {
        if (i.properties && i.properties._rug_swatches_bundle) return sum;
        if (i.handle === 'rug-swatches-bundle') return sum;
        if (i.handle === 'build-your-own-bundle') {
          if (byobCounted) return sum;
          byobCounted = true;
          return sum + (storedByobFull || i.original_line_price);
        }
        return sum + i.original_line_price;
      }, 0) + rugSwatchCount * RUG_SWATCH_CENTS;

    return Math.round(originalTotal * 0.045);
  }

  /* ============================================================
     CART API
     ============================================================ */
  const CartAPI = {
    async get() {
      const res = await fetch('/cart.js');
      return res.json();
    },
    async change(id, qty, line) {
      const body = line ? { line, quantity: qty } : { id, quantity: qty };
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      // Surface 4xx as a real error instead of returning Shopify's
      // `{ status, message, description }` payload as if it were a cart —
      // the latter has no `items` array, so callers that immediately
      // `_render()` would TypeError on `cart.items`. Mirrors the guard
      // that `add()` already has.
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || `cart/change.js failed (${res.status})`);
      }
      return res.json();
    },
    async add(id, qty = 1, properties = {}, sellingPlan = null, sections = null) {
      // Shopify expects the `items` array form whenever a selling_plan is set.
      // Legacy single-item form stays put for the existing qty-1 callers.
      const body = sellingPlan
        ? { items: [{ id: parseInt(id), quantity: qty, properties, selling_plan: parseInt(sellingPlan) }] }
        : { id: parseInt(id), quantity: qty, properties };
      // Bundled section rendering: the named sections come back rendered against
      // the post-add cart under `sections`, saving callers a second round trip.
      if (sections) body.sections = sections;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || `cart/add.js failed (${res.status})`);
      }
      return res.json();
    },
    /* Rewrite a line's properties in place. /cart/change.js replaces the whole
       properties object, so callers pass the merged set. Shopify re-keys the
       line (key = variant id + hash of properties) — use the returned cart,
       not any key the caller still holds. */
    async changeProperties(key, qty, properties) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty, properties }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || `cart/change.js failed (${res.status})`);
      }
      return res.json();
    },
    async updateNote(note) {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      return res.json();
    },
    async updateAttributes(attributes) {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      return res.json();
    },
    /* Bulk quantity update via /cart/update.js — pass { key1: 0, key2: 0, ... }
       to remove every bundle line in a single request. One round trip means
       Shopify keeps the bundle's discount allocations in sync; sequential
       /cart/change.js calls risked the bundle briefly existing in a 1-of-3
       state while the next delete was in flight. */
    async updateQuantities(updates) {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      return res.json();
    },
  };

  /* The four line-item properties that mark a pre-order line. Written by the
     PDP form, the quick-add / rec-card builders and the cart repair below;
     read by the Fulfil channel script (_restockDate), Flow and Klaviyo. */
  const PREORDER_PROPERTY_KEYS = ['Ship Date', '_restockDate', '_preorder', 'Product Batch ID'];

  /* ============================================================
     CART DRAWER CONTROLLER
     ============================================================ */
  const CartDrawer = {
    drawer: null,
    overlay: null,
    cart: null,
    isLoading: false,

    /* --- elements --- */
    el: {
      shippingMsg:    () => qs('#cdShippingMsg'),
      shippingFill:   () => qs('#cdShippingFill'),
      items:          () => qs('#cdItems'),
      recs:           () => qs('#cdRecs'),
      recsTrack:      () => qs('#cdRecsTrack'),
      recsPrev:       () => qs('#cdRecsPrev'),
      recsNext:       () => qs('#cdRecsNext'),

      // right panel states
      summaryPanel:   () => qs('#cdSummaryPanel'),
      fableInfo:      () => qs('#cdFableInfo'),

      // fable+ join
      fableJoin:      () => qs('#cdFableJoin'),
      fableJoinTitle: () => qs('#cdFableJoinTitle'),
      learnMore:      () => qs('#cdLearnMore'),
      fableInfoBack:  () => qs('#cdFableInfoBack'),

      // member state
      memberState:    () => qs('#cdMemberState'),
      memberGreeting: () => qs('#cdMemberGreeting'),
      memberDesc:     () => qs('#cdMemberDesc'),
      creditUseRow:   () => qs('#cdCreditUseRow'),
      creditBalance:  () => qs('#cdCreditBalance'),
      useNow:         () => qs('#cdUseNow'),
      creditRow:      () => qs('#cdCreditRow'),
      cancelCredit:   () => qs('#cdCancelCredit'),

      // ship opt
      shipOpt:        () => qs('#cdShipOpt'),
      shipDate:       () => qs('#cdShipDate'),
      yesSplit:       () => qs('#cdYesSplit'),
      noSplit:        () => qs('#cdNoSplit'),

      // price summary
      savingsRow:     () => qs('#cdSavingsRow'),
      savingsVal:     () => qs('#cdSavingsVal'),
      subtotalVal:    () => qs('#cdSubtotalVal'),
      protectionRow:  () => qs('#cdProtectionRow'),
      protectionVal:  () => qs('#cdProtectionVal'),
      creditSummRow:  () => qs('#cdCreditSummRow'),
      creditSummVal:  () => qs('#cdCreditSummVal'),

      // checkout
      checkoutBtn:    () => qs('#cdCheckoutBtn'),
      checkoutAlt:    () => qs('#cdCheckoutAlt'),
      checkoutTotal:  () => qs('#cdCheckoutTotal'),
    },

    /* Fable credit state tracking — available balance in cents (from Liquid
       metafield cfg.fableCredit). Drives whether the USE NOW row is shown
       on the member state. Apply-to-cart is intentionally handled by
       Subscribfy's own /checkout widget (OTP requirement), not by the drawer. */
    _fableCreditAmount: 0,

    /* Free-gift state — variants populated by _loadGiftVariants on init,
       `_giftBusy` is the in-flight write lock (so a second cart mutation
       arriving mid-add doesn't double-add the same gift line).
       `_giftSwatchCooldownUntil` is an anti-spam epoch ms gate on the
       swatch click handler, decoupled from the fetch lifecycle so the
       user feels a steady ~300ms rhythm even when the server is faster. */
    _giftVariants: null,
    _giftBusy: false,
    _preorderSyncInflight: null,
    _preorderSyncChecked: new Set(),
    _giftSwatchCooldownUntil: 0,

    /* ----------------------------------------------------------------
       INIT
    ---------------------------------------------------------------- */
    init() {
      this.drawer  = qs('#cartDrawerV2') || qs('.cart-drawer');
      this.overlay = qs('.cd-overlay');

      if (!this.drawer) return;

      // The Fable+ info backdrop sits inside `.cart-drawer` in the Liquid,
      // but `.cart-drawer` uses `transform` for its slide animation — a
      // transformed ancestor becomes the containing block for any
      // `position: fixed` descendant, clipping the backdrop to the drawer
      // instead of the viewport. Moving it to `<body>` at init lets
      // `position: fixed; inset: 0` cover the whole screen as intended.
      const backdrop = qs('#cdFableInfoBackdrop');
      if (backdrop && backdrop.parentElement !== document.body) {
        document.body.appendChild(backdrop);
      }

      this._bindClose();
      this._bindFablePlus();
      this._bindShipOpt();
      this._bindCheckout();
      this._watchActivation();
      this._bindExternalTriggers();
      this._interceptATCForms();
      this._interceptCartIcon();
      this._bindItemActionsOnce();
      this._bindRecsActionsOnce();
      this._bindBfcacheRestore();

      // Mirrors cart.js:602. The Shipping Protection app activates real
      // (~5% of cart) pricing only when this cart attribute is set; without
      // it the app falls back to a $0.01 placeholder line item. Fire-and-
      // forget so it does not block first paint of the drawer.
      if (cfg.insuranceEnabled && cfg.insuranceVariantId) {
        CartAPI.updateAttributes({ '_enable_insurance': 'true' })
          .catch(err => console.error('[CartDrawer] _enable_insurance attr', err));
      }

      // Prefetch cart so items render before first open
      this.refresh();

      // Prefetch gift product variants in parallel — the swatch row needs
      // them as soon as the threshold flips and we don't want a blank
      // placeholder on the first render. Result cached on the controller.
      // Skip on non-Canada stores: the IDs in FREE_GIFT only exist on the
      // CA catalog, the section fetch would 404, and we never want the
      // sync loop to fire on UK/US anyway.
      if (this._isGiftEnabledStore()) this._loadGiftVariants();
    },

    /* ----------------------------------------------------------------
       Reset UI when the page is restored from bfcache (browser back
       from /checkout). Chrome restores the DOM verbatim, which leaves
       the checkout button stuck on "PROCESSING…" and the busy overlay
       visible if either was active at nav time.

       Also strips the insurance line item that CHECKOUT+ added before
       navigating away — bfcache skips DOMContentLoaded so the global
       insurance-removal script (shipping-insurance-global.liquid)
       never runs, and refresh() would render the stale cart with
       protection still included in the total.
    ---------------------------------------------------------------- */
    _bindBfcacheRestore() {
      window.addEventListener('pageshow', async (e) => {
        if (!e.persisted) return;
        const btn = this.el.checkoutBtn();
        if (btn) {
          btn.disabled = false;
          if (btn.dataset.originalHtml) {
            btn.innerHTML = btn.dataset.originalHtml;
            delete btn.dataset.originalHtml;
          } else if (!qs('#cdCheckoutTotal', btn)) {
            // Fallback if original wasn't captured — rebuild the template.
            btn.innerHTML = 'CHECKOUT | <span id="cdCheckoutTotal"></span>';
          }
        }
        this._setDrawerBusy(false);
        this.isLoading = false;
        qsa('button[data-atc-loading="true"]').forEach(b => b.removeAttribute('data-atc-loading'));
        await this._stripInsuranceIfPresent();
        this.refresh();
      });
    },

    /* ----------------------------------------------------------------
       Remove the shipping-insurance line item if it lingers in the cart
       outside the /checkout flow. Used by the bfcache restore handler.
    ---------------------------------------------------------------- */
    async _stripInsuranceIfPresent() {
      if (!cfg.insuranceEnabled) return;
      try {
        const cart = await CartAPI.get();
        const insuranceItem = cart.items?.find(i => i.handle === 'shipping-insurance');
        if (insuranceItem) await CartAPI.change(insuranceItem.key, 0);
      } catch (err) {
        console.error('[CartDrawer] strip insurance error', err);
      }
    },

    /* ----------------------------------------------------------------
       FREE GIFT (GWP)
       ----
       One-shot variant prefetch on init keeps the swatch row paint-ready
       the moment the threshold flips — without it the first sync would
       show an empty placeholder until the product fetch resolved.
       Result cached at module scope; subsequent re-renders read from it.
    ---------------------------------------------------------------- */
    async _loadGiftVariants() {
      if (this._giftVariants) return this._giftVariants;
      try {
        // Section Rendering API — sections/cart-gift-swatches.liquid emits
        // a JSON blob with native Shopify swatch metadata (option_value
        // .swatch.color / .swatch.image), which `/products/{handle}.js`
        // does NOT expose. Using product.js would force us to fall back to
        // variant.featured_image (the variant's product photo) — way too
        // detailed for a 15px circle.
        const res = await fetch('/?sections=cart-gift-swatches');
        if (!res.ok) return null;
        const payload = await res.json();
        const html = payload['cart-gift-swatches'] || '';
        const doc  = new DOMParser().parseFromString(html, 'text/html');
        const node = doc.querySelector('#cartGiftSwatchesData');
        if (!node) return null;

        const data = JSON.parse(node.textContent);
        this._giftVariants = (data.variants || []).map(v => ({
          id:        v.id,
          title:     v.title,
          available: v.available,
          color:     v.swatchColor || '',
          image:     v.swatchImage || '',
          // Variant product photo — used for the optimistic image swap on
          // swatch click, so the line image flips before the cart round-trip.
          featuredImage: v.featuredImage || '',
        }));

        // If the gift row is already on screen with the empty placeholder
        // (first render landed before the section fetch resolved), force a
        // re-render so swatches paint in. Invalidate the items-signature
        // cache because the visible cart didn't actually change — only our
        // local variant cache did, so the signature would otherwise short-
        // circuit the re-render in _renderItems.
        if (this.cart && this.drawer?.querySelector('.cd-item__gift-swatches[data-pending]')) {
          this._lastItemsSig = null;
          this._renderItems(this.cart);
        }
        return this._giftVariants;
      } catch (err) {
        console.warn('[CartDrawer] gift variants fetch failed', err);
        return null;
      }
    },

    /* Identify the gift line strictly by the `_free_gift` property — NOT by
       product_id alone. A product_id-only fallback used to bridge legacy
       lines, but it mis-tagged a manually-added paid carafe as the gift,
       which caused the duplicate's REMOVE button to vanish (the render
       path keys off this same property). With strict matching, any carafe
       without `_free_gift` is treated as a normal paid line and gets the
       full qty/REMOVE bottom row. */
    _findGiftLine(cart) {
      const items = cart?.items || [];
      return items.find(i =>
        i.product_id === FREE_GIFT.productId &&
        (i.properties?.[FREE_GIFT.propertyFlag] === 'true' || i.properties?.[FREE_GIFT.propertyFlag] === true)
      ) || null;
    },

    _findAllGiftLines(cart) {
      const items = cart?.items || [];
      return items.filter(i =>
        i.product_id === FREE_GIFT.productId &&
        (i.properties?.[FREE_GIFT.propertyFlag] === 'true' || i.properties?.[FREE_GIFT.propertyFlag] === true)
      );
    },

    /* Eligible subtotal = sum of visible items' original_line_price, minus
       the gift line itself. Uses original (pre-Fable+-discount) line totals
       on purpose: the marketing copy on the Fable+ tiered discount badge
       reads "Fable+: 35% Off ($350+)", which keys off the shelf-cart total
       BEFORE the discount applies. Using `final_line_price` here put the
       gift in a catch-22 — Fable+ trims the cart below $350, removing the
       gift, even though the shopper's actual shelf cart was over $350.
       Mirrors `_visibleItems` (so membership / insurance drop out) and
       excludes ONLY the `_free_gift`-flagged line — paid duplicates of
       the same product (someone genuinely buying a second carafe) count
       toward the threshold like any other purchase. */
    // _eligibleSubtotal(cart) {
    //   const items = this._visibleItems(cart);
    //   return items.reduce((sum, i) => {
    //     const isGift = i.product_id === FREE_GIFT.productId &&
    //                    (i.properties?.[FREE_GIFT.propertyFlag] === 'true' ||
    //                     i.properties?.[FREE_GIFT.propertyFlag] === true);
    //     if (isGift) return sum;
    //     return sum + (i.original_line_price || i.line_price || 0);
    //   }, 0);
    // },

    _eligibleSubtotal(cart) {
      const excluded = new Set(FREE_GIFT.excludedProductIds ?? []);
      const items = this._visibleItems(cart);
      return items.reduce((sum, i) => {
        if (excluded.has(i.product_id)) return sum;
        const isGift = i.product_id === FREE_GIFT.productId &&
            (i.properties?.[FREE_GIFT.propertyFlag] === 'true' ||
                i.properties?.[FREE_GIFT.propertyFlag] === true);
        if (isGift) return sum;
        return sum + (i.original_line_price || i.line_price || 0);
      }, 0);
    },

    /* Reconcile gift presence with the threshold rule. Idempotent, race-safe
       via `_giftBusy`: a second invocation while a write is in flight returns
       the input cart unchanged — the in-flight write's resolution will trigger
       its own re-render through the caller chain. Returns either the input
       cart (no change) or the post-write cart from /cart/add|change. */
    /* Store-level gate. The same theme bundle ships to all three Fable
       stores (CA/UK/US — see [[multi-store-architecture]]), and the gift
       product IDs are resolved per-domain at module load. A null FREE_GIFT
       means the current store is not in the rollout — every gift code path
       short-circuits here so we never auto-add a non-existent variant ID
       or render an empty swatch row. To add a store, append to
       FREE_GIFT_BY_DOMAIN and configure the discount rule on that admin. */
    _isGiftEnabledStore() {
      // return FREE_GIFT !== null;
      return false;
    },

    /* Mirror the discount's entry condition: Fable+ member OR membership product
       already in cart. cfg.isMember reads the Exison Active-tag metafield
       (theme side); _findMembershipLine catches a guest who just clicked
       JOIN NOW in this session (the membership variant lands in cart before
       any tag flips). Both signals together cover member + lapsed-member-
       re-joining scenarios — anything outside that gets no gift even at
       $350+. See [[fable-plus-detection]] for the tag split. */
    _isGiftEligibleCustomer(cart) {
      if (!FREE_GIFT.memberOnly) return true;
      if (cfg.isMember) return true;
      if (this._findMembershipLine(cart)) return true;
      return false;
    },

    async _syncFreeGift(cart) {
      return cart;
      if (!cart || this._giftBusy) return cart;
      // Hard kill-switch for UK/US. Returning untouched cart on non-CAD
      // shops means no auto-add, no dedupe, no render-time gift detection
      // ever fires off-Canada.
      if (!this._isGiftEnabledStore()) return cart;

      const giftLines  = this._findAllGiftLines(cart);
      const eligible   = this._eligibleSubtotal(cart);
      const eligibleCustomer = this._isGiftEligibleCustomer(cart);
      const shouldHave = eligibleCustomer && eligible >= FREE_GIFT.thresholdCents;

      // Cleanup branch — if a previous swap got interrupted (the add
      // succeeded but the remove never landed, e.g. tab backgrounded
      // mid-flight) the cart ends up with TWO _free_gift lines. Render
      // would then treat both as gift and neither would have a REMOVE
      // button. Collapse the duplicates to a single line before the
      // shouldHave/remove logic runs.
      if (giftLines.length > 1) {
        this._giftBusy = true;
        try {
          const updates = {};
          for (let i = 1; i < giftLines.length; i++) updates[giftLines[i].key] = 0;
          await CartAPI.updateQuantities(updates);
          cart = await CartAPI.get();
        } catch (err) {
          console.error('[CartDrawer] gift dedupe error', err);
        } finally {
          this._giftBusy = false;
        }
      }

      const giftLine = this._findGiftLine(cart);

      if (shouldHave && !giftLine) {
        this._giftBusy = true;
        try {
          // /cart/add.js with the single-item body returns the *line item*
          // (key/variant_id/etc), NOT the cart. Returning it straight to
          // the caller hands `_render` a "cart" with no `items` array, so
          // the first renderer that iterates `cart.items` (`_renderShipOpt`)
          // explodes. Refetch the cart explicitly — mirrors what
          // `_addRecommendation` / `_addMembership` already do.
          await CartAPI.add(FREE_GIFT.defaultVariantId, 1, {
            [FREE_GIFT.propertyFlag]: 'true',
          });
          return await CartAPI.get();
        } catch (err) {
          console.error('[CartDrawer] gift add error', err);
          return cart;
        } finally {
          this._giftBusy = false;
        }
      }

      if (!shouldHave && giftLine) {
        this._giftBusy = true;
        try {
          return await CartAPI.change(giftLine.key, 0);
        } catch (err) {
          console.error('[CartDrawer] gift remove error', err);
          return cart;
        } finally {
          this._giftBusy = false;
        }
      }

      return cart;
    },

    /* ============================================================
       PRE-ORDER PROPERTY REPAIR
       A pre-order line should reach the cart carrying the four properties
       the PDP form attaches (`Ship Date`, `_restockDate`, `_preorder`,
       `Product Batch ID`). The PDP re-renders those inputs asynchronously
       on variant switch and never disables Add-to-cart while that fetch
       is in flight, so a fast click submits the form without them
       (CA #253586). Line properties are immutable once the order exists,
       so the cart is the last place they can still be fixed.

       sections/cart-preorder-data.liquid re-derives the promise per line
       with the PDP's own gate and every line is reconciled against it:
       missing properties are added, a previous variant's dates are
       corrected, and dates on a variant that is no longer a pre-order are
       removed (a stale future `_restockDate` would otherwise park the
       order in Fulfil until that date). Kits are reconciled on their own
       variant; BYOB (`_grouping`) and rug-swatch lines are left alone,
       their promise lives on the group, not the component. Every key is
       checked once per page load, and on the add-to-cart path the section
       arrives bundled with the /cart/add.js response, so the common case
       costs no extra request at all.
       ============================================================ */
    async _fetchPreorderPromises(html) {
      if (html == null) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(`${root}?section_id=cart-preorder-data`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`cart-preorder-data section failed (${res.status})`);
        html = await res.text();
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.getElementById('cdPreorderData');
      const byKey = {};
      for (const row of node ? JSON.parse(node.textContent) : []) byKey[row.key] = row;
      return byKey;
    },

    _preorderRepairCandidates(cart) {
      return (cart?.items || []).filter(i =>
        !i.properties?._grouping &&
        !i.properties?._rug_swatches_bundle &&
        i.product_type !== 'Parent Bundle' &&
        !this._preorderSyncChecked.has(i.key)
      );
    },

    /* The properties a line should carry for the promise the section reports;
       empty when the variant has no live promise. */
    _desiredPreorderProps(promise) {
      if (!promise || !promise.restock_date) return {};
      const props = { '_preorder': 'true', '_restockDate': promise.restock_date };
      if (promise.ship_date) props['Ship Date'] = promise.ship_date;
      if (promise.batch_id) props['Product Batch ID'] = promise.batch_id;
      return props;
    },

    async _syncPreorderProps(cart, sectionHtml) {
      if (!cart) return cart;
      // A concurrent caller (two add-to-carts in quick succession) waits for the
      // in-flight pass instead of skipping — that pass may have re-keyed lines,
      // so re-read the cart (and drop the now-stale bundled section) first.
      if (this._preorderSyncInflight) {
        try { await this._preorderSyncInflight; } catch (_) { /* logged by its owner */ }
        try { cart = await CartAPI.get(); } catch (_) { return cart; }
        sectionHtml = undefined;
      }
      const candidates = this._preorderRepairCandidates(cart);
      if (!candidates.length) return cart;

      const run = (async () => {
        const promises = await this._fetchPreorderPromises(sectionHtml);
        let latest = cart;
        for (const item of candidates) {
          // Marked before the write so a persistent 4xx can't retry on every render.
          this._preorderSyncChecked.add(item.key);
          const promise = promises[item.key];
          if (!promise) continue; // not evaluated by the section (bundle child): leave alone
          const desired = this._desiredPreorderProps(promise);
          const current = item.properties || {};
          if (!PREORDER_PROPERTY_KEYS.some(k => (current[k] ?? '') !== (desired[k] ?? ''))) continue;
          const properties = { ...current };
          for (const k of PREORDER_PROPERTY_KEYS) delete properties[k];
          Object.assign(properties, desired);
          try {
            // /cart/change.js returns the whole cart, so the last write doubles
            // as the refetch.
            latest = await CartAPI.changeProperties(item.key, item.quantity, properties);
          } catch (err) {
            console.error('[CartDrawer] preorder property repair error', item.key, err);
          }
        }
        // Everything in the post-write cart was just reconciled; remember the
        // new keys too so the next render does not re-fetch the section for them.
        if (latest !== cart) for (const i of latest.items || []) this._preorderSyncChecked.add(i.key);
        return latest;
      })();
      this._preorderSyncInflight = run;
      try {
        return await run;
      } catch (err) {
        console.error('[CartDrawer] preorder property repair error', err);
        return cart;
      } finally {
        if (this._preorderSyncInflight === run) this._preorderSyncInflight = null;
      }
    },

    /* Swap the gift's variant in-place. Shopify's /cart/change.js can't
       repoint a line at a different variant_id, so this is a remove + add
       under the same `_giftBusy` lock that the auto-sync uses — preventing
       a stray sync from re-adding the default variant between the two
       requests. Optimistic UI mark is applied by the click handler before
       we get here so the coral ring lands in <16ms. */
    async _swapGiftVariant(newVariantId) {
      if (this._giftBusy) return this.cart;
      // Defensive — sync wouldn't have rendered a gift swatch row off-CAD,
      // but bail anyway in case a stale `_free_gift` property survived a
      // cross-store sync of the cart and the swatch row somehow paints.
      if (!this._isGiftEnabledStore()) return this.cart;
      const original = this._findGiftLine(this.cart);
      if (!original || original.variant_id === newVariantId) return this.cart;

      this._giftBusy = true;
      try {
        // Add-then-remove keeps the gift visible if the add fails (sold out,
        // invalid variant): the original line stays put and we just log out.
        await CartAPI.add(newVariantId, 1, {
          [FREE_GIFT.propertyFlag]: 'true',
        });
        // Refetch + locate the stale line by variant_id, NOT by the pre-add
        // `original.key`. Shopify can reshuffle keys when a new line is
        // appended (we've seen 400s on /cart/change.js when handing back a
        // pre-add key after an add that touched the same product). Walking
        // the fresh items list and matching `_free_gift` + non-new variant
        // is robust to that: even if the cart ends up with multiple stale
        // gift lines, dedupe in `_syncFreeGift` cleans up next render.
        const fresh = await CartAPI.get();
        const stale = (fresh.items || []).find(i =>
          i.product_id === FREE_GIFT.productId &&
          i.variant_id !== newVariantId &&
          (i.properties?.[FREE_GIFT.propertyFlag] === 'true' ||
           i.properties?.[FREE_GIFT.propertyFlag] === true)
        );
        if (stale) {
          return await CartAPI.change(stale.key, 0);
        }
        return fresh;
      } catch (err) {
        console.error('[CartDrawer] gift swap error', err);
        try { return await CartAPI.get(); } catch { return this.cart; }
      } finally {
        this._giftBusy = false;
      }
    },

    /* ----------------------------------------------------------------
       BIND ITEM/REC ACTIONS — delegate from drawer root ONCE.
       Previously re-bound in each _renderItems, which stacked listeners
       and caused a single REMOVE click to fire N times → whole cart cleared.
    ---------------------------------------------------------------- */
    _bindItemActionsOnce() {
      const root = this.drawer;

      root.addEventListener('click', async (e) => {
        const minus  = e.target.closest('.cd-qty-minus');
        const plus   = e.target.closest('.cd-qty-plus');
        const remove = e.target.closest('.cd-item__remove');
        const addRec = e.target.closest('.cd-rec-card__add-btn');

        if (minus) {
          e.preventDefault();
          const { key, qty, line } = minus.dataset;
          await this._changeQty(key, parseInt(qty), parseInt(line) || null);
        } else if (plus) {
          e.preventDefault();
          const { key, qty, line } = plus.dataset;
          await this._changeQty(key, parseInt(qty), parseInt(line) || null);
        } else if (remove) {
          e.preventDefault();
          const bundleKeys = remove.dataset.bundleKeys;
          if (bundleKeys) {
            // Bundle: wipe every line belonging to it in one /cart/update.js
            // request. Falling back to the single-line flow only when no
            // bundle-keys attribute is present keeps the code path for
            // singles untouched.
            await this._removeBundle(bundleKeys.split(',').filter(Boolean));
          } else {
            const { key, line } = remove.dataset;
            await this._changeQty(key, 0, parseInt(line) || null);
          }
        } else if (addRec) {
          e.preventDefault();
          await this._addRecommendation(addRec);
        }

        const toggle = e.target.closest('.cd-rec-card__swatch-toggle');
        if (toggle) {
          e.preventDefault();
          e.stopPropagation();
          const dropdown = toggle.closest('.cd-rec-card__swatch-group')?.querySelector('.cd-rec-card__swatch-dropdown');
          if (dropdown) dropdown.classList.toggle('is-open');
        }

        // Free-gift swatch — swap the gift line's variant. Scope-checked via
        // `.cd-item--gift` so rec-card swatch clicks (also `.cd-swatch-btn`)
        // never route here. Optimistic UI: paint the coral ring + flip the
        // line image + flip the variant subtitle in the same frame, before
        // awaiting the round-trip; the post-swap _render reconciles from
        // server truth. _renderItems will short-circuit on an unchanged
        // signature if nothing meaningful changed, leaving our optimistic
        // DOM in place.
        const giftSwatch = e.target.closest('.cd-item--gift .cd-item__gift-swatch-btn');
        if (giftSwatch) {
          e.preventDefault();
          if (giftSwatch.classList.contains('is-selected')) return;
          if (giftSwatch.dataset.soldOut === 'true') return;
          // Cooldown — fake throttle so a rapid double-click can't queue
          // two visual swaps in the same paint. Independent from the fetch
          // promise: the network may complete faster, but the UI keeps the
          // disabled state until the cooldown elapses so the swatch row
          // feels deliberate instead of jittery.
          if (Date.now() < this._giftSwatchCooldownUntil) return;
          const newVariantId = parseInt(giftSwatch.dataset.variantId);
          if (!newVariantId) return;

          const COOLDOWN_MS = 2000;
          this._giftSwatchCooldownUntil = Date.now() + COOLDOWN_MS;
          const swatchRow = giftSwatch.closest('.cd-item__gift-swatches');
          if (swatchRow) swatchRow.classList.add('is-busy');

          const row = giftSwatch.closest('.cd-item--gift');
          // Match the qty/remove flow's loading affordance (`_setItemLoading`
          // sets opacity .5) so the cart reads the swap as "this line is
          // updating" — same visual language a customer already learned from
          // every other line in the drawer. Cleared on the same 300ms tick
          // as the swatch cooldown.
          if (row) row.style.opacity = '0.5';
          setTimeout(() => {
            const busyRow = this.drawer?.querySelector('.cd-item__gift-swatches.is-busy');
            if (busyRow) busyRow.classList.remove('is-busy');
            const giftRow = this.drawer?.querySelector('.cd-item--gift');
            if (giftRow) giftRow.style.opacity = '';
          }, COOLDOWN_MS);

          if (row) {
            row.querySelectorAll('.cd-item__gift-swatch-btn.is-selected')
               .forEach(b => b.classList.remove('is-selected'));
            giftSwatch.classList.add('is-selected');

            // Look up the picked variant's image + title from the prefetched
            // section data and flip the on-screen image + variant label in
            // the same paint. The opacity dim above runs concurrently so
            // the row reads as "updating" while these values change — same
            // affordance as `_setItemLoading` uses for qty / remove flows.
            const variant = (this._giftVariants || []).find(v => v.id === newVariantId);
            if (variant) {
              if (variant.featuredImage) {
                const img = row.querySelector('.cd-item__img');
                if (img) {
                  // Match `_buildItemRow`'s 120x suffix so the image source
                  // matches what the eventual server-truth re-render uses
                  // (otherwise the browser would re-decode a different URL
                  // when the post-swap render lands).
                  const url = variant.featuredImage.replace('http:', 'https:').replace(/(\.[a-z]+)$/, '_120x120$1');
                  img.src = url;
                }
              }
              if (variant.title) {
                const variantEl = row.querySelector('.cd-item__variant');
                if (variantEl) variantEl.textContent = variant.title;
              }
            }
          }

          try {
            const cart = await this._swapGiftVariant(newVariantId);
            if (cart) {
              this.cart = cart;
              this._render(cart);
              this._updateCartCount(cart);
            }
          } catch (err) {
            console.error('[CartDrawer] gift swatch click error', err);
          }
        }
      });

      root.addEventListener('change', async (e) => {
        const input = e.target.closest('.cd-qty__num');
        if (!input) return;
        const qty = Math.max(0, parseInt(input.value) || 0);
        await this._changeQty(input.dataset.key, qty, parseInt(input.dataset.line) || null);
      });
    },

    /* ----------------------------------------------------------------
       WATCH FOR DRAWER OPEN (existing theme adds `active` class)
    ---------------------------------------------------------------- */
    _watchActivation() {
      // Track previous active state so we only react to `active` transitions,
      // NOT every class-list mutation. Without this guard, toggling any other
      // class on the drawer (`is-busy` during ship-opt / Fable+ / credit
      // updates) fires an unwanted `refresh()` mid-request — which renders
      // with stale cart data and makes the radio visually flip back for a
      // frame before the real update lands.
      let wasActive = this.drawer.classList.contains('active');
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type !== 'attributes' || m.attributeName !== 'class') continue;
          const isActive = this.drawer.classList.contains('active');
          if (isActive === wasActive) continue;
          wasActive = isActive;
          if (isActive) {
            // Also applies the header offset for the external-trigger path
            // (theme.js flipping `active` via data-active-name="cart"), so
            // the drawer opens under the site header whether it was fired
            // by our `open()` or by the legacy jQuery flow.
            this._updateHeaderOffset();
            document.body.classList.add('cart-drawer-open');
            this.refresh();
          } else {
            document.body.classList.remove('cart-drawer-open');
            // theme.js's ajaxCart.load() (rug swatches) opens the drawer with the header's tinted
            // overlay and a body scroll lock that only its own close button undoes; the overlay
            // click and close() leave both behind.
            if (!document.querySelector('[data-active-name].active:not([data-active-name="cart"]), #nav-overlay.active')) {
              document.querySelectorAll('[data-header-overlay]').forEach(el => el.classList.add('hide'));
              document.body.classList.remove('overflow--hidden');
            }
            document.querySelectorAll('[data-active-target="cart"].isSelected').forEach(el => el.classList.remove('isSelected'));
          }
        }
      });
      observer.observe(this.drawer, { attributes: true });

      // Also handle overlay click to close
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }
    },

    /* ----------------------------------------------------------------
       OPEN DRAWER
    ---------------------------------------------------------------- */
    open() {
      if (!this.drawer) return;
      this._updateHeaderOffset();
      this.drawer.classList.add('active');
      this.drawer.setAttribute('aria-expanded', 'true');
      document.body.classList.add('cart-drawer-open');
      // Keep the offset correct across viewport / sticky-header changes while
      // the drawer is visible. Resize-only is enough because theme.js locks
      // body scroll on open (overflow--hidden), so the header's viewport-
      // relative position doesn't shift under the user mid-session.
      if (!this._headerOffsetBound) {
        window.addEventListener('resize', () => this._updateHeaderOffset(), { passive: true });
        this._headerOffsetBound = true;
      }
    },

    /* Measure the site header's visible bottom edge and expose it to CSS via
       `--cd-top-offset`, so the drawer starts under the header instead of
       covering it. `getBoundingClientRect().bottom` gives viewport-relative
       position, which already accounts for an announcement bar, transparent-
       header offset, or mid-page scroll. Clamp at 0 in case the header has
       scrolled above the viewport. */
    _updateHeaderOffset() {
      if (!this.drawer) return;
      const header = document.querySelector('.site-header');
      const bottom = header ? header.getBoundingClientRect().bottom : 0;
      this.drawer.style.setProperty('--cd-top-offset', Math.max(0, bottom) + 'px');
    },

    /* ----------------------------------------------------------------
       CLOSE
    ---------------------------------------------------------------- */
    close() {
      this.drawer.classList.remove('active');
      this.drawer.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('cart-drawer-open');
      this._hideCartError();
    },

    /* ----------------------------------------------------------------
       CART ERROR BANNER
       Surfaces a rejected add-to-cart request (e.g. a Shopify Function
       validation error) to the shopper instead of silently opening an
       unchanged/empty drawer.
    ---------------------------------------------------------------- */
    _showCartError(message) {
      const banner = qs('#cdErrorBanner');
      if (!banner || !message) return;
      banner.textContent = message;
      banner.style.display = 'block';
    },
    _hideCartError() {
      const banner = qs('#cdErrorBanner');
      if (!banner) return;
      banner.style.display = 'none';
      banner.textContent = '';
    },

    /* ----------------------------------------------------------------
       BIND CLOSE BUTTONS
    ---------------------------------------------------------------- */
    _bindClose() {
      // main close button
      this.drawer.addEventListener('click', (e) => {
        const closeBtn = e.target.closest('[data-action="close"], .cd-header__close, #cdClose');
        if (closeBtn) {
          e.preventDefault();
          this.close();
        }

        // Info tooltip toggle (shipping protection etc.)
        const infoBtn = e.target.closest('.cd-info-btn');
        if (infoBtn) {
          e.preventDefault();
          e.stopPropagation();
          const wasOpen = infoBtn.classList.contains('is-open');
          qsa('.cd-info-btn.is-open', this.drawer).forEach(b => b.classList.remove('is-open'));
          if (!wasOpen) infoBtn.classList.add('is-open');
          return;
        }

        // Click outside any open tooltip closes it
        qsa('.cd-info-btn.is-open', this.drawer).forEach(b => b.classList.remove('is-open'));
      });
    },

    /* ----------------------------------------------------------------
       BIND FABLE+ PANEL TOGGLE + JOIN/NOT_TODAY RADIOS
    ---------------------------------------------------------------- */
    _bindFablePlus() {
      this.drawer.addEventListener('click', (e) => {
        if (e.target.closest('#cdLearnMore')) {
          e.preventDefault();
          this._showFableInfo();
        }
      });

      // Bind BACK on document, not on `.cart-drawer`. On mobile we
      // teleport the info panel (which holds #cdFableInfoBack) out of
      // `.cart-drawer` and into <body> to fix modal centering — clicks
      // on the back button would otherwise never reach a listener on
      // the drawer.
      document.addEventListener('click', (e) => {
        if (e.target.closest('#cdFableInfoBack')) {
          e.preventDefault();
          e.stopPropagation();
          this._hideFableInfo();
        }
      });

      this.drawer.addEventListener('change', (e) => {
        const radio = e.target.closest('input[name="cdFablePlus"]');
        if (!radio) return;
        if (radio.value === 'join')       this._addMembership();
        else if (radio.value === 'not_today') this._removeMembership();
      });
    },

    /* ----------------------------------------------------------------
       FABLE+ MEMBERSHIP — add/remove via JOIN NOW / NOT TODAY radios.
       Adding posts the variant with its selling_plan so Shopify treats it
       as a subscription. The Exison (Subscribfy) app's Discount Function
       then applies member pricing on top, which shows up in cart.total_discounts
       and cascades into the "You're saving" row on the next render.
    ---------------------------------------------------------------- */
    _findMembershipLine(cart) {
      const vid = parseInt(cfg.membershipVariantId) || 0;
      if (!vid || !cart?.items?.length) return null;
      return cart.items.find(i => i.variant_id === vid) || null;
    },

    _setFableRadiosLoading(loading) {
      qsa('input[name="cdFablePlus"]', this.drawer).forEach(r => { r.disabled = loading; });
    },

    _setDrawerBusy(busy) {
      if (this.drawer) this.drawer.classList.toggle('is-busy', !!busy);
    },

    /* No-op while a membership add/remove is mid-flight. Any intermediate
       render (e.g. a `cart:updated`/`ajaxCart.afterAddItem` listener firing
       a refresh() concurrent with _addMembership's own CartAPI.get) would
       otherwise flip the radio back to the stale cart's state for a few
       frames — the "flicker behind the overlay" the user reported. */
    _syncFableRadios(cart) {
      if (this._membershipBusy) return;
      const inCart = !!this._findMembershipLine(cart);
      const join = qs('input[name="cdFablePlus"][value="join"]', this.drawer);
      const no   = qs('input[name="cdFablePlus"][value="not_today"]', this.drawer);
      if (join) join.checked = inCart;
      if (no)   no.checked   = !inCart;
    },

    async _addMembership() {
      const variantId = parseInt(cfg.membershipVariantId) || 0;
      const planId    = parseInt(cfg.membershipPlanId)    || 0;
      if (!variantId || !planId) {
        console.warn('[CartDrawer] membership not configured (variantId/planId missing)');
        this._syncFableRadios(this.cart);
        return;
      }
      if (this._findMembershipLine(this.cart)) return;

      this._membershipBusy = true;
      this._setFableRadiosLoading(true);
      this._setDrawerBusy(true);
      try {
        await CartAPI.add(variantId, 1, {}, planId);
        this.cart = await CartAPI.get();
        this.cart = await this._syncFreeGift(this.cart);
        // Clear the flag BEFORE rendering so _syncFableRadios reflects the
        // freshly-fetched cart (which now has the membership line).
        this._membershipBusy = false;
        this._render(this.cart);
        this._updateCartCount(this.cart);
        document.dispatchEvent(new CustomEvent('subscribfy:VIPAddedToCart'));
      } catch (err) {
        console.error('[CartDrawer] add membership error', err);
        this._membershipBusy = false;
        this._syncFableRadios(this.cart);
      } finally {
        this._membershipBusy = false;
        this._setFableRadiosLoading(false);
        this._setDrawerBusy(false);
      }
    },

    async _removeMembership() {
      const line = this._findMembershipLine(this.cart);
      if (!line) return;

      this._membershipBusy = true;
      this._setFableRadiosLoading(true);
      this._setDrawerBusy(true);
      try {
        let cart = await CartAPI.change(line.key, 0);
        cart = await this._syncFreeGift(cart);
        this.cart = cart;
        this._membershipBusy = false;
        this._render(cart);
        this._updateCartCount(cart);
        document.dispatchEvent(new CustomEvent('subscribfy:VIPRemovedFromCart'));
      } catch (err) {
        console.error('[CartDrawer] remove membership error', err);
        this._membershipBusy = false;
        this._syncFableRadios(this.cart);
      } finally {
        this._membershipBusy = false;
        this._setFableRadiosLoading(false);
        this._setDrawerBusy(false);
      }
    },

    _showFableInfo() {
      const info = this.el.fableInfo();
      const backdrop = qs('#cdFableInfoBackdrop');

      // On mobile the info panel is a centered modal. `.cart-drawer` uses
      // `transform` for its slide-in animation, which turns it into the
      // containing block for any `position: fixed` descendant — centering
      // resolves to the drawer's box instead of the viewport and the modal
      // lands off-center + can scroll with the drawer. Teleporting to
      // <body> while the modal is open makes `position: fixed; top/left:
      // 50%` reference the viewport like intended. Desktop keeps the
      // modal inside `.cd-right` because the desktop styling is a panel
      // slide-in (position: absolute relative to that parent), which
      // needs the original DOM location.
      if (info && window.matchMedia('(max-width: 989px)').matches) {
        if (info.parentElement !== document.body) {
          info._cdOrigParent = info.parentElement;
          info._cdOrigNext   = info.nextSibling;
          document.body.appendChild(info);
        }
      }

      if (info) {
        info.classList.add('is-open');
        info.setAttribute('aria-hidden', 'false');
      }
      if (backdrop) {
        backdrop.classList.add('is-open');
        if (!backdrop._bound) {
          backdrop.addEventListener('click', () => this._hideFableInfo());
          backdrop._bound = true;
        }
      }
    },

    _hideFableInfo() {
      const info = this.el.fableInfo();
      const backdrop = qs('#cdFableInfoBackdrop');
      if (info) {
        info.classList.remove('is-open');
        info.setAttribute('aria-hidden', 'true');
      }
      if (backdrop) backdrop.classList.remove('is-open');

      // Return the info panel to its original spot so desktop styling
      // (which positions it inside .cd-right) keeps working if the user
      // resizes from mobile back to desktop with the drawer still open.
      if (info && info._cdOrigParent) {
        info._cdOrigParent.insertBefore(info, info._cdOrigNext || null);
        info._cdOrigParent = null;
        info._cdOrigNext   = null;
      }
    },

    /* ----------------------------------------------------------------
       BIND SHIP OPT RADIO BUTTONS — mirrors legacy assets/cart.js:48-65
       (writes cart.note = "shipasap" | "preorder:shiplater"). Uses the
       same busy overlay + radio-disable treatment as Fable+ so the user
       can't double-toggle during the /cart/update.js round-trip.
    ---------------------------------------------------------------- */
    _bindShipOpt() {
      this.drawer.addEventListener('change', async (e) => {
        if (e.target.name !== 'cdSplitShip') return;
        const radios = qsa('input[name="cdSplitShip"]', this.drawer);
        radios.forEach(r => { r.disabled = true; });
        this._setDrawerBusy(true);
        try {
          await CartAPI.updateNote(e.target.value);
          // Refetch so cart.note + any note-dependent rendering stays in
          // sync, and so a rapid second toggle sees the resolved state.
          this.cart = await CartAPI.get();
          this._render(this.cart);
        } catch (err) {
          console.error('[CartDrawer] ship-opt update error', err);
          // Revert radio state to whatever the cart actually has
          const current = this.cart?.note || '';
          const desired = current.includes('shiplater') ? 'preorder:shiplater' : 'shipasap';
          radios.forEach(r => { r.checked = r.value === desired; });
        } finally {
          radios.forEach(r => { r.disabled = false; });
          this._setDrawerBusy(false);
        }
      });
    },

    /* ----------------------------------------------------------------
       BIND CHECKOUT BUTTONS
    ---------------------------------------------------------------- */
    _bindCheckout() {
      this.drawer.addEventListener('click', (e) => {
        const withBtn    = e.target.closest('#cdCheckoutBtn');
        const withoutBtn = e.target.closest('#cdCheckoutAlt');

        if (withBtn && !withBtn.disabled) {
          e.preventDefault();
          this._doCheckout(true);
        }
        if (withoutBtn) {
          e.preventDefault();
          this._doCheckout(false);
        }
      });
    },

    async _doCheckout(withProtection) {
      const btn = this.el.checkoutBtn();
      if (btn) {
        // Preserve the inner "CHECKOUT | <span id=cdCheckoutTotal>$X</span>"
        // structure so _updateCheckoutBtn can still target the total span when
        // we restore from bfcache after a browser back from /checkout.
        if (!btn.dataset.originalHtml) btn.dataset.originalHtml = btn.innerHTML;
        btn.disabled   = true;
        btn.textContent = 'PROCESSING…';
      }

      try {
        // Subscribfy gift card takes priority over generic applied_discount since
        // it represents real customer store credit; the generic discount is a
        // marketing code that can be re-entered at checkout if overridden.
        const giftCard = localStorage.getItem('subscribfy_gift_card_code');
        const discount = localStorage.getItem('applied_discount');
        const code = (giftCard && giftCard !== 'null') ? giftCard
                   : (discount && discount !== 'null') ? discount
                   : '';
        const baseUrl = code
          ? `/checkout?discount=${encodeURIComponent(code)}`
          : '/checkout';

        if (cfg.insuranceEnabled && cfg.insuranceVariantId) {
          const insuranceInCart = this.cart?.items?.find(i => i.handle === 'shipping-insurance');
          if (withProtection && !insuranceInCart) {
            await CartAPI.add(cfg.insuranceVariantId, 1, { '_shipping_protection': 'yes' });
          } else if (!withProtection && insuranceInCart) {
            await CartAPI.change(insuranceInCart.key, 0);
          }
        }

        if (discount && discount !== 'null') localStorage.removeItem('applied_discount');
        window.location.href = baseUrl;
      } catch (err) {
        console.error('[CartDrawer] Checkout error', err);
        if (btn) {
          btn.disabled = false;
          if (btn.dataset.originalHtml) {
            btn.innerHTML = btn.dataset.originalHtml;
            delete btn.dataset.originalHtml;
          }
          this._updateCheckoutBtn();
        }
      }
    },

    /* ----------------------------------------------------------------
       INTERCEPT ALL ATC FORM SUBMISSIONS (capture phase)
       Catches any form[action*="/cart/add"] before jQuery or theme.js
    ---------------------------------------------------------------- */
    _interceptATCForms() {
      document.addEventListener('submit', async (e) => {
        const form = e.target;
        if (!form || !form.getAttribute) return;
        const action = form.getAttribute('action') || '';
        if (!action.includes('/cart/add')) return;

        // Take full ownership — stop jQuery ajaxCart from also firing
        e.preventDefault();
        e.stopImmediatePropagation();

        const formData = new FormData(form);
        const id = formData.get('id');
        if (!id) return;

        const qty = Math.max(1, parseInt(formData.get('quantity') || '1') || 1);

        // Collect line item properties (properties[Name] fields). Empty values are
        // skipped: quick-add blanks its preorder inputs when the shopper switches to a
        // non-preorder variant, and forwarding those would stamp four empty attributes
        // on the line (visible as a bare "Ship Date:" in cart/checkout).
        const properties = {};
        for (const [key, val] of formData.entries()) {
          if (val !== '' && key.startsWith('properties[') && key.endsWith(']')) {
            properties[key.slice(11, -1)] = val;
          }
        }

        // On the PDP the variant <select> carries every variant's promise from page
        // load (data-preorder / data-restock / data-batch), while the hidden property
        // inputs are re-rendered asynchronously on variant switch and Add-to-cart does
        // not wait for that fetch. The option the shopper actually selected is the one
        // source that is always current at submit time, so it wins over whatever the
        // inputs still hold: nothing (CA #253586) or the previous variant's dates (a
        // Dove Gray set promised Speckled White's batch). Templates whose options do
        // not carry data-restock at all keep the form's own properties untouched.
        let derivedFromOption = false;
        const variantSelect = form.querySelector('select[name="id"]');
        if (variantSelect && variantSelect.querySelector('option[data-restock]')) {
          const option = [...variantSelect.options].find(o => o.value == id);
          if (option) {
            derivedFromOption = true;
            for (const k of PREORDER_PROPERTY_KEYS) delete properties[k];
            if (option.dataset.restock) {
              properties['_restockDate'] = option.dataset.restock;
              properties['_preorder'] = 'true';
              if (option.dataset.preorder) properties['Ship Date'] = option.dataset.preorder;
              if (option.dataset.batch) properties['Product Batch ID'] = option.dataset.batch;
            }
          }
        }

        // stopImmediatePropagation above also kills theme.js's own submit handler
        // (theme.js:6700-6748), which is where quick-add forms normally get their
        // preorder properties built from window.productVariants. Rebuild the same
        // four properties here so preorder line items survive the intercept.
        // Never overwrite anything the form itself already supplied.
        if (!properties['_restockDate']) {
          const productId = form.dataset.productId || form.closest('[data-product-id]')?.dataset.productId;
          const variants = productId && window.productVariants && window.productVariants[productId];
          const variant = variants && variants.find(v => v.id == id);
          if (variant && variant.preorder_status) {
            if (!properties['Ship Date']) properties['Ship Date'] = variant.preorder_status;
            if (!properties['_preorder']) properties['_preorder'] = 'true';
            if (variant.restock_date) properties['_restockDate'] = variant.restock_date;
            if (variant.batch_id && !properties['Product Batch ID']) properties['Product Batch ID'] = variant.batch_id;
          }
        }

        // Visual feedback on the ATC button while we work
        const atcBtn = form.querySelector('[type="submit"]') || form.querySelector('button');
        const prevDisabled = atcBtn?.disabled || false;
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.setAttribute('data-atc-loading', 'true');
        }

        // Block MutationObserver's refresh during open(); we'll render manually
        this.isLoading = true;
        this._hideCartError();

        // Hoisted so the catch block can tell a rejected add (show the reason)
        // apart from a failure *after* a successful add (stay silent, as
        // before — the item is already in the real cart at that point).
        let added;
        try {
          // 1. Add to cart. A line built from the variant <option> is correct by
          //    construction, so its key is marked checked and no promise section is
          //    rendered at all — the PDP path costs exactly one add + one cart fetch.
          //    Forms without that data (quick-add, landing pages) get the section
          //    bundled into the add response instead, still no extra round trip.
          added = await CartAPI.add(id, qty, properties, null, derivedFromOption ? null : 'cart-preorder-data');
          if (derivedFromOption && added && added.key) this._preorderSyncChecked.add(added.key);

          // 2. Fetch updated cart and render — do not go through refresh()'s isLoading guard
          this.cart = await CartAPI.get();
          this.cart = await this._syncFreeGift(this.cart);
          this.cart = await this._syncPreorderProps(this.cart, added && added.sections ? added.sections['cart-preorder-data'] : undefined);
          this._render(this.cart);
          this._updateCartCount(this.cart);
          // Recs are not on the critical path
          this._loadRecs();

          // 3. Open drawer with items already populated
          this.open();
        } catch (err) {
          console.error('[CartDrawer] ATC intercept error', err);
          // Only surface a customer-facing message when the add itself was
          // rejected (e.g. a Shopify Function validation error like "Canyon
          // Clay items are exclusive to Fable+ members"). If `added` is set,
          // the item is already in the real cart and this failed on a later
          // step (fetch/render/sync) — keep the original silent fail-open
          // behavior rather than showing a confusing error for a successful add.
          if (!added) this._showCartError(err && err.message);
          this.open();
        } finally {
          this.isLoading = false;
          if (atcBtn) {
            atcBtn.disabled = prevDisabled;
            atcBtn.removeAttribute('data-atc-loading');
          }
        }
      }, true); // capture = true
    },

    /* ----------------------------------------------------------------
       INTERCEPT CART ICON CLICKS
       Toggles the drawer — opens it when closed, closes it when open.
       Mobile relies on this because the in-drawer close button is hidden
       (see cart-drawer.css mobile block); the cart icon in the site
       header is the sole dismissal affordance.
    ---------------------------------------------------------------- */
    _interceptCartIcon() {
      document.addEventListener('click', (e) => {
        // Match the cart icon or any plain "/cart" link that isn't a checkout/api link
        const cartLink = e.target.closest(
          '#cartIcon, .cart-link, [data-open-cart], a[href="/cart"]'
        );
        if (!cartLink) return;

        // If theme.js will handle it via data-active-name, let it do so
        if (cartLink.dataset.activeName === 'cart' || cartLink.closest('[data-active-name="cart"]')) return;

        e.preventDefault();
        e.stopPropagation();
        if (this.drawer && this.drawer.classList.contains('active')) {
          this.close();
        } else {
          this.open();
          this.refresh();
        }
      });
    },

    /* ----------------------------------------------------------------
       BIND EXTERNAL TRIGGERS (qty, remove, recs add)
    ---------------------------------------------------------------- */
    _bindExternalTriggers() {
      // Listen for items added elsewhere (PDP, etc.)
      document.body.addEventListener('ajaxCart.afterAddItem', () => {
        if (this.drawer.classList.contains('active')) this.refresh();
      });

      // Also listen for cart:updated custom event
      document.addEventListener('cart:updated', () => {
        if (this.drawer.classList.contains('active')) this.refresh();
      });

      // Gift-note add/remove flow — the Giftnote app fires
      // `giftnote_saved` / `giftnote_removed` on document when the
      // shopper saves a message. On the legacy /cart page
      // `assets/cart.js:395-447` handles the actual POST to
      // /cart/add.js, but it reads the variant id from
      // `#cartGifting.data-product` (cart.js:27-28) — and `#cartGifting`
      // lives in `sections/main-cart-footer.liquid`, so it only exists
      // on /cart. On every other page `productData = null` and cart.js
      // silently POSTs `{ id: null }` (400). That left the drawer with
      // nothing to refresh — user-visible symptom: Save does nothing.
      //
      // Fix: do the add ourselves, gated on the legacy element missing
      // so /cart page still runs exactly one add. Use
      // cfg.giftNoteVariantId (shop.metafields.giftnote.product…variants[0].id).
      const handleGiftSaved = async () => {
        const legacyHandled = !!document.getElementById('cartGifting');
        const vid = parseInt(cfg.giftNoteVariantId) || 0;
        try {
          if (!legacyHandled && vid) {
            const cart = await CartAPI.get();
            const exists = cart.items.some(i => i.variant_id === vid);
            if (!exists) await CartAPI.add(vid, 1);
          }
        } catch (err) {
          console.error('[CartDrawer] gift-note add error', err);
        }
        await this._postGiftRefresh();
      };

      const handleGiftRemoved = async () => {
        const legacyHandled = !!document.getElementById('cartGifting');
        const vid = parseInt(cfg.giftNoteVariantId) || 0;
        try {
          if (!legacyHandled && vid) {
            const cart = await CartAPI.get();
            const line = cart.items.find(i => i.variant_id === vid);
            if (line) await CartAPI.change(line.key, 0);
          }
        } catch (err) {
          console.error('[CartDrawer] gift-note remove error', err);
        }
        await this._postGiftRefresh();
      };

      document.addEventListener('giftnote_saved', handleGiftSaved);
      document.addEventListener('giftnote_removed', handleGiftRemoved);
    },

    /* ----------------------------------------------------------------
       REFRESH — fetch cart & re-render
    ---------------------------------------------------------------- */
    async refresh() {
      if (this.isLoading) return;
      this.isLoading = true;
      this._showLoading();

      try {
        this.cart = await CartAPI.get();
        this.cart = await this._syncFreeGift(this.cart);
        this.cart = await this._syncPreorderProps(this.cart);
        this._render(this.cart);
        this._loadRecs();
      } catch (err) {
        console.error('[CartDrawer] refresh error', err);
      } finally {
        this.isLoading = false;
      }
    },

    _showLoading() {
      const items = this.el.items();
      if (!items) return;
      // Keep previously rendered items visible while refetching to avoid a blank flicker
      const hasRenderedItems = items.querySelector('.cd-item') || items.querySelector('.cd-empty');
      if (hasRenderedItems) return;
      items.innerHTML = '<div class="cd-items-loading"><div class="spinner"></div></div>';
    },

    /* ----------------------------------------------------------------
       RENDER
    ---------------------------------------------------------------- */
    _render(cart) {
      this._renderShippingBar(cart);
      this._renderItems(cart);
      this._renderRightPanel(cart);
      this._ensureCompareAtPrices(cart);
    },

    // /cart.js doesn't return variant.compare_at_price, so for items added
    // after initial page load, fetch the product JSON (cached per handle)
    // and populate window.cartCompareAtPrices so strikethrough pricing
    // renders in the drawer.
    _ensureCompareAtPrices(cart) {
      const items = this._visibleItems(cart);
      const map = (window.cartCompareAtPrices = window.cartCompareAtPrices || {});
      const missing = items.filter(i => !(i.key in map) && i.handle);
      if (!missing.length) return;

      const productCache = (window.__cdProductCache = window.__cdProductCache || {});
      const handles = [...new Set(missing.map(i => i.handle))];

      Promise.all(handles.map(h => {
        if (productCache[h] !== undefined) return productCache[h];
        return fetch(`/products/${h}.js`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
          .then(data => (productCache[h] = data));
      })).then(() => {
        let changed = false;
        for (const item of missing) {
          const product = productCache[item.handle];
          const variant = product?.variants?.find(v => v.id === item.variant_id);
          const cmp = variant?.compare_at_price || 0;
          if (map[item.key] !== cmp) {
            map[item.key] = cmp;
            changed = true;
          }
        }
        if (changed && this.cart === cart) {
          this._lastItemsSig = null;
          this._renderItems(cart);
          this._renderRightPanel(cart);
        }
      });
    },

    /* ---- Shipping Bar ---- */
    _renderShippingBar(cart) {
      const msg  = this.el.shippingMsg();
      const fill = this.el.shippingFill();
      if (!msg || !fill) return;

      // Fable+ members (active subscribers) or carts containing the Fable+
      // product both qualify for free shipping with no threshold — per the
      // Subscribfy Free Shipping function (Shipping Threshold: No limit).
      // Reflect that here so we don't show "YOU'RE $X AWAY" to customers
      // who already qualify.
      const hasFablePlusInCart = !!this._findMembershipLine(cart);
      if (cfg.isMember || hasFablePlusInCart) {
        fill.style.width = '100%';
        msg.textContent  = "YOU'VE GOT FREE SHIPPING WITH FABLE+";
        return;
      }

      const total     = cart.total_price;
      const threshold = cfg.freeShippingThreshold;
      const pct       = Math.min((total / threshold) * 100, 100);

      fill.style.width = pct + '%';

      if (total >= threshold) {
        msg.textContent = "YOU'VE GOT FREE SHIPPING";
      } else {
        const remaining = formatMoney(threshold - total);
        msg.textContent = `YOU'RE ${remaining} AWAY FROM FREE SHIPPING`;
      }
    },

    /* ---- Cart Items ----
       Central filter for what renders as `.cd-item` in the left panel.
       Excludes two kinds of internal items from view:
        • Fable+ membership variant (matched by cfg.membershipVariantId)
        • Shipping insurance product (matched by cfg.insuranceVariantId
          AND by handle 'shipping-insurance' as a defensive fallback
          — variant id comes from a shop metafield that merchants can
          clear; the handle is the hard guarantee from Shopify).
       Detection code elsewhere (e.g. price summary row, checkout flow,
       cache invalidation) intentionally reads raw cart.items instead of
       this filtered view — those queries need to find these hidden items
       to render their prices / toggle add/remove. */
    _visibleItems(cart) {
      const membershipVid = parseInt(cfg.membershipVariantId) || 0;
      const insuranceVid  = parseInt(cfg.insuranceVariantId)  || 0;
      const items = cart?.items || [];
      return items.filter(i =>
        i.variant_id !== membershipVid &&
        i.variant_id !== insuranceVid &&
        i.handle     !== 'shipping-insurance'
      );
    },

    _itemsSignature(cart) {
      const items = this._visibleItems(cart);
      if (!items.length) return `empty:${cart?.item_count ?? 0}`;
      return items
        .map(i => `${i.key}:${i.quantity}:${i.final_line_price || i.line_price}:${i.properties?.['Ship Date'] || ''}`)
        .join('|');
    },

    _renderItems(cart) {
      const container = this.el.items();
      if (!container) return;

      // Skip re-render if cart items haven't changed — prevents <img> re-decode flash
      const sig = this._itemsSignature(cart);
      if (sig === this._lastItemsSig && container.querySelector('.cd-item, .cd-empty')) {
        return;
      }
      this._lastItemsSig = sig;

      const visibleItems = this._visibleItems(cart);

      if (!visibleItems.length) {
        container.innerHTML = `
          <div class="cd-empty">
            <p class="cd-empty__title">Your cart is empty.</p>
            <a class="cd-empty__link" href="/collections/all">Continue Shopping</a>
          </div>`;
        return;
      }

      const compareAtPrices = window.cartCompareAtPrices || {};
      const saleDiscounts   = window.cartSaleDiscounts   || {};

      const bundles = {};
      const singles = [];

      for (const item of visibleItems) {
        const groupKey = item.properties?._grouping;
        if (groupKey) {
          if (!bundles[groupKey]) bundles[groupKey] = [];
          bundles[groupKey].push(item);
        } else {
          singles.push(item);
        }
      }

      let html = '';

      // Render bundles
      for (const [, bItems] of Object.entries(bundles)) {
        html += this._buildBundleRow(bItems);
      }

      // Render singles
      for (const item of singles) {
        html += this._buildItemRow(item, compareAtPrices, saleDiscounts);
      }

      container.innerHTML = html;
      // Listeners are bound once on the drawer root in init(); do not re-bind here.
    },

    _buildItemRow(item, compareAtPrices, saleDiscounts) {
      const imgUrl     = item.featured_image?.url || item.image || '';
      const imgSrc     = imgUrl ? imgUrl.replace('http:', 'https:').replace(/(\.[a-z]+)$/, '_120x120$1') : '';
      const name       = item.product_title || item.title;
      const variant    = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      const preorder   = item.properties?.['Ship Date'] || null;
      // Gift Message is a virtual product — the Giftnote app manages its
      // lifecycle via the SEND/EDIT GIFT NOTE flow, not through the cart.
      // Shoppers shouldn't be able to click through to the product page
      // (there isn't a meaningful PDP for it) or change its quantity (it
      // only ever exists as a single $3 add-on), so we treat it like a
      // bundle row: non-clickable title, no qty stepper.
      const giftNoteVid = parseInt(cfg.giftNoteVariantId) || 0;
      const isGiftNote  = giftNoteVid && item.variant_id === giftNoteVid;
      // Free-gift line — auto-managed by _syncFreeGift. Swap the bottom row
      // (qty stepper + REMOVE) for an inline swatch picker, suppress the
      // variant subtitle (the swatches communicate the same info visually),
      // and stamp a FREE GIFT badge on the title block. Off-Canada the
      // store-level gate forces this false so any cart line that somehow
      // carries the `_free_gift` property (cross-store sync edge case)
      // renders as a normal paid line — REMOVE button + qty stepper.
      const isFreeGift  = this._isGiftEnabledStore() && (
                          item.properties?.[FREE_GIFT.propertyFlag] === 'true' ||
                          item.properties?.[FREE_GIFT.propertyFlag] === true);
      const showQty    = item.product_type !== 'Parent Bundle' && !isGiftNote && !isFreeGift;

      // Pricing
      const compareAt = compareAtPrices[item.key] || item.variant?.compare_at_price || 0;
      const saleDiscount = saleDiscounts[item.key];
      const unitPrice  = item.final_price || item.price;
      const linePrice  = item.final_line_price || item.line_price;

      // Line-level discount (Shopify Discount Function — the Fable+ 10% lives here).
      const lineAllocations = item.line_level_discount_allocations || [];
      const lineDiscountCents = item.total_discount || lineAllocations.reduce((s, a) => s + (a.amount || 0), 0);
      const hasLineDiscount = lineDiscountCents > 0;
      const lineDiscountTitle = lineAllocations[0]?.discount_application?.title || 'Fable+ Discount';

      let priceRow = '';
      if (saleDiscount && saleDiscount !== '') {
        priceRow = `
          <span class="cd-item__compare">${formatMoney(compareAt || item.original_price)}</span>
          <span class="cd-item__price">${cfg.currencySymbol}${saleDiscount}</span>`;
      } else if (hasLineDiscount) {
        priceRow = `
          <span class="cd-item__compare">${formatMoney(item.original_line_price)}</span>
          <span class="cd-item__price">${formatMoney(linePrice)}</span>`;
      } else if (compareAt && compareAt > unitPrice) {
        priceRow = `
          <span class="cd-item__compare">${formatMoney(compareAt * item.quantity)}</span>
          <span class="cd-item__price">${formatMoney(linePrice)}</span>`;
      } else {
        priceRow = `<span class="cd-item__price">${formatMoney(linePrice)}</span>`;
      }

      const badgeHtml = hasLineDiscount ? `
        <span class="cd-item__fable-badge">
          ${this._esc(lineDiscountTitle)}
        </span>` : '';

      const imgWrap = isGiftNote
        ? `<img class="cd-item__img" src="${imgSrc}" alt="${this._esc(name)}" width="112" height="112" loading="eager" decoding="async">`
        : `<a class="cd-item__img-link" href="${item.url || '/products/' + item.handle}" tabindex="-1">
             <img class="cd-item__img" src="${imgSrc}" alt="${this._esc(name)}" width="112" height="112" loading="eager" decoding="async">
           </a>`;

      const nameHtml = isGiftNote
        ? this._esc(name)
        : `<a href="${item.url || '/products/' + item.handle}">${this._esc(name)}</a>`;

      const giftBadge   = '';
      const variantLine = !variant ? '' : `<p class="cd-item__variant">${this._esc(variant)}</p>`;
      const bottomRow   = isFreeGift
        ? this._buildGiftSwatchRow(item)
        : `<div class="cd-item__bottom">
             <button class="cd-item__remove" data-key="${item.key}" data-line="${item.line_idx || ''}" aria-label="Remove ${this._esc(name)} from cart">
               REMOVE
             </button>
             ${showQty ? this._buildQty(item) : ''}
           </div>`;

      const rowClass = isFreeGift ? 'cd-item cd-item--gift' : 'cd-item';

      return `
        <div class="${rowClass}" data-key="${item.key}" data-line="${item.line_idx || ''}">
          ${imgWrap}
          <div class="cd-item__body">
            <div class="cd-item__title-block">
              ${giftBadge}
              <p class="cd-item__name">${nameHtml}</p>
              ${variantLine}
            </div>
            <div class="cd-item__prices-col">
              <div class="cd-item__prices-row">${priceRow}</div>
              ${badgeHtml}
            </div>
            ${preorder ? `<span class="cd-item__preorder">Ship Date: ${this._esc(preorder)}</span>` : ''}
            ${bottomRow}
          </div>
        </div>`;
    },

    /* Inline swatch row for the free-gift line. Mirrors the rec-card visual
       (`.cd-swatch-btn` — 15×15 circle, coral ring on selected) but lives
       inside `.cd-item--gift` instead of a recommendation card, so the
       click delegation in _bindItemActionsOnce scopes to .cd-item--gift
       and never clashes with the rec slider's own swatch handlers. */
    _buildGiftSwatchRow(item) {
      const variants = this._giftVariants;
      if (!variants || !variants.length) {
        // Variants still resolving — render an empty placeholder. The next
        // _render after the fetch settles will swap in the real swatches.
        return '<div class="cd-item__gift-swatches" data-pending="true"></div>';
      }
      const selectedId = item.variant_id;
      const buttons = variants.map(v => {
        const selected = v.id === selectedId ? ' is-selected' : '';
        const soldOut  = v.available ? '' : ' data-sold-out="true"';
        // Priority mirrors PDP / rec-card behaviour:
        //   1. swatch.color  — fast solid fill, native Shopify swatch metadata
        //   2. swatch.image  — only when the merchant chose an image swatch
        //   3. CSS color name from the variant title (`Sage` → sage, etc.)
        //      so products without configured swatches still get *something*
        //      reasonable instead of a transparent ring.
        // Use the `background` shorthand (NOT `background-color`) so the
        // inline style also resets `background-clip` to its initial
        // `border-box` value — matches what cart-rec-swatch-btn.liquid
        // emits for upsell swatches. Without the reset, `.cd-swatch-btn`'s
        // `background-clip: padding-box` makes the colour fill stop at the
        // inner edge of the (transparent) 1px border, leaving an unwanted
        // parent-bg ring between fill and the coral box-shadow.
        let bg = '';
        if (v.color) {
          bg = `background: ${v.color};`;
        } else if (v.image) {
          bg = `background: url('${v.image.replace('http:', 'https:')}') center/cover;`;
        } else {
          const fallback = (v.title || '').toLowerCase().replace(/[^a-z]/g, '');
          bg = `background: ${fallback || '#DEDEDD'};`;
        }
        return `<button type="button"
                        class="cd-swatch-btn cd-item__gift-swatch-btn${selected}"
                        data-variant-id="${v.id}"
                        title="${this._esc(v.title)}"
                        aria-label="${this._esc(v.title)}"
                        ${soldOut}
                        style="${bg}"></button>`;
      }).join('');
      return `<div class="cd-item__gift-swatches">${buttons}</div>`;
    },

    _buildBundleRow(items) {
      const first    = items[0];
      const imgUrl   = first.featured_image?.url || first.image || '';
      const imgSrc   = imgUrl ? imgUrl.replace('http:', 'https:').replace(/(\.[a-z]+)$/, '_120x120$1') : '';
      const bundleName = first.properties?.Bundle || first.product_title || first.title;
      const bundleImg  = first.properties?._bundleImg || imgSrc;

      const totalLine = items.reduce((s, i) => s + (i.final_line_price || i.line_price), 0);
      const totalOrig = items.reduce((s, i) => s + i.original_line_price, 0);
      const hasDisc   = totalOrig > totalLine;
      const rawBundleTitle =
        items.flatMap(i => i.line_level_discount_allocations || [])[0]
          ?.discount_application?.title || 'Fable+ Discount';
      // Simplify any "NN% bundle discount" / "BYOB Bundle Discount" variants
      // emitted by the byob-tiers function to a cleaner "NN% Off" badge.
      const bundleDiscountTitle = rawBundleTitle
        .replace(/(\d+(?:\.\d+)?)\s*%\s*(?:bundle|byob)\s*discount/i, '$1% Off')
        .replace(/^BYOB\s+Bundle\s+Discount$/i, 'Bundle Discount');

      return `
        <div class="cd-item cd-item--bundle" data-key="${first.key}">
          <img class="cd-item__img" src="${bundleImg}" alt="${this._esc(bundleName)}" width="112" height="112" loading="eager" decoding="async">
          <div class="cd-item__body">
            <p class="cd-item__name">${this._esc(bundleName)}</p>
            ${items.map(i => {
              const shipDate = i.properties?.['Ship Date'] || '';
              // Emit variant + ship-date as direct grid children (not
              // inside a .cd-item__title-block flex wrapper) so the
              // ship date inherits the same `grid-column: 1 / -1`
              // full-row treatment that single items already get.
              // Inside the wrapper it was clipped to the 1fr title
              // column and long dates like "August 14, 2026" wrapped.
              return `
                <p class="cd-item__variant">${this._esc(i.product_title)} × ${i.quantity}</p>
                ${shipDate ? `<span class="cd-item__preorder">Ship Date: ${this._esc(shipDate)}</span>` : ''}
              `;
            }).join('')}
            <div class="cd-item__prices-col">
              <div class="cd-item__prices-row">
                ${hasDisc ? `<span class="cd-item__compare">${formatMoney(totalOrig)}</span>` : ''}
                <span class="cd-item__price">${formatMoney(totalLine)}</span>
              </div>
              ${hasDisc ? `
                <span class="cd-item__fable-badge">
                  ${this._esc(bundleDiscountTitle)}
                </span>` : ''}
            </div>
            <div class="cd-item__bottom">
              <button class="cd-item__remove"
                      data-key="${first.key}"
                      data-line="${first.line_idx || ''}"
                      data-bundle-keys="${items.map(i => i.key).join(',')}"
                      aria-label="Remove ${this._esc(bundleName)} from cart">
                REMOVE
              </button>
            </div>
          </div>
        </div>`;
    },

    _buildQty(item) {
      return `
        <div class="cd-qty">
          <button class="cd-qty__btn cd-qty-minus" data-key="${item.key}" data-qty="${Math.max(0, item.quantity - 1)}" data-line="${item.line_idx || ''}" aria-label="Decrease quantity">
            <span class="icon-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="1" viewBox="0 0 11 1" fill="none"><path d="M0 0.6875V0H11V0.6875H0Z" fill="currentColor"/></svg>
            </span>
          </button>
          <input class="cd-qty__num" type="number" value="${item.quantity}" min="0" data-key="${item.key}" data-line="${item.line_idx || ''}" aria-label="Quantity">
          <button class="cd-qty__btn cd-qty-plus" data-key="${item.key}" data-qty="${item.quantity + 1}" data-line="${item.line_idx || ''}" aria-label="Increase quantity">
            <span class="icon-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M5.15625 5.15625V0H5.84375V5.15625H11V5.84375H5.84375V11H5.15625V5.84375H0V5.15625H5.15625Z" fill="currentColor"/></svg>
            </span>
          </button>
        </div>`;
    },

    async _changeQty(key, qty, line) {
      this._setItemLoading(key, true);

      // Optimistic UI: update the qty input + its sibling +/- data-qty attrs
      // immediately so the user sees the click land in <16ms instead of after
      // the /cart/change.js round-trip (~300-500ms, longer with Fable+ on).
      // The full _render below will overwrite this with server-truth data.
      const row     = qs(`[data-key="${key}"]`, this.drawer);
      const qtyInput = row?.querySelector('.cd-qty__num');
      const prevQty  = parseInt(qtyInput?.value) || null;
      if (qtyInput && qty > 0) {
        qtyInput.value = qty;
        const minus = row.querySelector('.cd-qty-minus');
        const plus  = row.querySelector('.cd-qty-plus');
        if (minus) minus.dataset.qty = Math.max(0, qty - 1);
        if (plus)  plus.dataset.qty  = qty + 1;
      }

      const prevFirstPid = this.cart?.items?.[0]?.product_id;
      const prevCount    = this.cart?.items?.length ?? 0;

      try {
        let cart = await CartAPI.change(key, qty, line);
        cart = await this._syncFreeGift(cart);
        this.cart = cart;
        this._render(cart);
        this._updateCartCount(cart);

        // Recs only depend on firstItem.product_id + the cartProductIds
        // filter. Skip the _loadRecs work entirely when neither changed —
        // typical qty +/- hits this fast path. On remove (count changes) we
        // also invalidate cache so the removed product becomes eligible.
        const newFirstPid = cart.items?.[0]?.product_id;
        if (newFirstPid !== prevFirstPid || cart.items.length !== prevCount) {
          this._recsCache = null;
          this._loadRecs();
        }
      } catch (err) {
        console.error('[CartDrawer] qty change error', err);
        if (qtyInput && prevQty != null) qtyInput.value = prevQty;
      } finally {
        this._setItemLoading(key, false);
      }
    },

    _setItemLoading(key, loading) {
      const item = qs(`[data-key="${key}"]`, this.drawer);
      if (item) item.style.opacity = loading ? '0.5' : '1';
    },

    /* Refresh after a gift-note add/remove. Bypasses the isLoading guard
       in `refresh()` because we've just awaited our own add/change and
       know the server state is current — if the drawer was opened mid-
       add, the guard would otherwise swallow this render. */
    async _postGiftRefresh() {
      try {
        this.cart = await CartAPI.get();
        this.cart = await this._syncFreeGift(this.cart);
        this._render(this.cart);
        this._updateCartCount(this.cart);
        this._recsCache = null;
        this._loadRecs();
      } catch (err) {
        console.error('[CartDrawer] gift-note refresh error', err);
      }
    },

    /* Remove every cart line belonging to a single bundle in one request.
       The bundle row's data-bundle-keys attr lists all keys; we zero each
       via /cart/update.js's `updates` bulk payload so Shopify's discount
       allocations stay consistent (the old cart used the same approach).
       Only the representative (first) row has the DOM node, which is
       what .cd-item uses for its opacity loading state. */
    async _removeBundle(keys) {
      if (!keys?.length) return;
      const firstKey = keys[0];
      this._setItemLoading(firstKey, true);

      const prevFirstPid = this.cart?.items?.[0]?.product_id;
      const prevCount    = this.cart?.items?.length ?? 0;

      try {
        const updates = {};
        for (const k of keys) updates[k] = 0;
        await CartAPI.updateQuantities(updates);
        // /cart/update.js returns the patched cart but without the indexed
        // line_idx our code expects; re-fetch through the instrumented
        // CartAPI.get so indexCartLines runs.
        this.cart = await CartAPI.get();
        this.cart = await this._syncFreeGift(this.cart);
        this._render(this.cart);
        this._updateCartCount(this.cart);

        const newFirstPid = this.cart.items?.[0]?.product_id;
        if (newFirstPid !== prevFirstPid || this.cart.items.length !== prevCount) {
          this._recsCache = null;
          this._loadRecs();
        }
      } catch (err) {
        console.error('[CartDrawer] bundle remove error', err);
      } finally {
        this._setItemLoading(firstKey, false);
      }
    },

    async _addRecommendation(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      // Card-wide overlay handles the "busy" signal (see .cd-rec-card.is-loading
      // in cart-drawer.css). The ATC button stays visually unchanged — no text
      // swap, no inline spinner, no :disabled greying. The ::before overlay
      // blocks pointer events so a second click can't reach the button.
      const card = btn.closest('.cd-rec-card');
      if (card) card.classList.add('is-loading');
      try {
        const properties = this._recPreorderProperties(card, variantId);
        await CartAPI.add(variantId, 1, properties);
        this.cart = await CartAPI.get();
        this.cart = await this._syncFreeGift(this.cart);
        this._render(this.cart);
        this._updateCartCount(this.cart);
        // The just-added product should drop out of the slider (we filter by
        // cartProductIds in _loadRecs). Invalidate the 60s cache so the next
        // _loadRecs rebuilds with the fresh filter, otherwise the cache-hit
        // path would show the ADD+ card still available for this product.
        this._recsCache = null;
        this._loadRecs();
      } catch (err) {
        console.error('[CartDrawer] add recommendation error', err);
        if (card) card.classList.remove('is-loading');
      }
    },

    /* ---- Right panel ---- */
    _renderRightPanel(cart) {
      this._renderFableJoin(cart);
      this._renderShipOpt(cart);
      this._renderPriceSummary(cart);
      this._updateCheckoutBtn(cart);
    },

    _renderFableJoin(cart) {
      if (cfg.isMember) {
        // Show member state, hide join section
        const fableJoin   = this.el.fableJoin();
        const memberState = this.el.memberState();
        if (fableJoin)   fableJoin.style.display   = 'none';
        if (memberState) memberState.style.display  = '';

        // Available balance — from Liquid-injected metafield (dollars → cents).
        // Apply-to-cart flow is handled by Subscribfy's checkout widget (OTP
        // requirement) so we only surface the balance here, never write to cart.
        this._fableCreditAmount = parseInt(cfg.fableCredit) || 0;

        const useRow     = this.el.creditUseRow();
        const balanceEl  = this.el.creditBalance();
        const appliedRow = this.el.creditRow();

        if (this._fableCreditAmount > 0) {
          if (useRow) {
            useRow.style.display = '';
            if (balanceEl) balanceEl.textContent = formatMoney(this._fableCreditAmount);
          }
          if (appliedRow) appliedRow.style.display = 'none';
        } else {
          // Member without balance — hide both rows
          if (useRow)     useRow.style.display     = 'none';
          if (appliedRow) appliedRow.style.display = 'none';
        }
      } else {
        // Reflect cart state in the JOIN NOW / NOT TODAY radios. _render runs
        // after every cart mutation, so this keeps the UI in sync if the
        // membership is added/removed via any path (drawer radio, legacy
        // widget, direct API).
        this._syncFableRadios(cart);

        // Potential saving: if membership is already in cart, show what the
        // Exison cart transform has already applied; otherwise show 10% of
        // the pre-discount items subtotal as a preview of what joining
        // unlocks. Use line-level savings (original_line_price vs.
        // final_line_price) instead of cart.total_discounts because the
        // Fable+ discount lives at the line level — cart.total_discounts is
        // often 0 even though every item shows a strikethrough price, which
        // previously left the title stuck at the bare "JOIN FABLE+ TODAY,
        // AND SAVE." until the shopper toggled NOT TODAY.
        const inCart   = !!this._findMembershipLine(cart);
        const baseline = cart.items_subtotal_price || cart.total_price;
        const lineSavings = (cart.items || []).reduce((sum, i) => {
          const orig  = i.original_line_price != null ? i.original_line_price : (i.line_price || 0);
          const final = i.final_line_price   != null ? i.final_line_price   : (i.line_price || 0);
          return sum + Math.max(0, orig - final);
        }, 0);
        const potentialSaving = inCart
          ? Math.max(lineSavings, cart.total_discounts || 0)
          : Math.round(baseline * 0.1);
        const fableJoinTitle = this.el.fableJoinTitle();
        if (fableJoinTitle && potentialSaving > 0) {
          fableJoinTitle.textContent = `JOIN FABLE+ TODAY, AND SAVE ${formatMoney(potentialSaving)}.`;
        }
      }
    },

    _renderShipOpt(cart) {
      const shipOpt = this.el.shipOpt();
      if (!shipOpt) return;

      const preorderDates = [];
      for (const item of cart.items) {
        const d = item.properties?.['Ship Date'];
        if (d && !preorderDates.includes(d)) preorderDates.push(d);
      }

      if (preorderDates.length) {
        shipOpt.style.display = '';
        // Show the latest date
        preorderDates.sort((a, b) => new Date(b) - new Date(a));
        const shipDateEl = this.el.shipDate();
        if (shipDateEl) shipDateEl.textContent = preorderDates[0];
      } else {
        shipOpt.style.display = 'none';
      }

      // Keep radio state in sync with cart.note on every render, so if any
      // other flow (Subscribfy, external /cart/update.js) mutates the note
      // the drawer UI reflects the real server state.
      const note = cart.note || '';
      const desired = note.includes('shiplater') ? 'preorder:shiplater' : 'shipasap';
      qsa('input[name="cdSplitShip"]', this.drawer).forEach(r => {
        r.checked = r.value === desired;
      });
    },

    _renderPriceSummary(cart) {
      const savingsRow     = this.el.savingsRow();
      const savingsVal     = this.el.savingsVal();
      const subtotalVal    = this.el.subtotalVal();
      const protRow        = this.el.protectionRow();
      const protVal        = this.el.protectionVal();
      const creditSummRow  = this.el.creditSummRow();

      // --- "You're saving" breakdown — three non-overlapping sources:
      //
      //   1. lineSavings:  original_line_price - final_line_price per line.
      //      Covers EVERY line-level drop the shopper can see (Shopify Discount
      //      Function, Fable+ 10% off, BYOB bundle variant-pricing, per-product
      //      promo codes). Authoritative because it matches the strikethrough
      //      prices rendered on each item.
      //
      //   2. cartLevelExtra: cart.total_discounts minus lineSavings (floor 0).
      //      Picks up cart-scope discounts that don't attribute to any single
      //      line (e.g. "$5 off subtotal"). Floored to avoid double-counting
      //      when Shopify reports fewer cart-level discounts than the lines show
      //      (which happens when bundle savings are custom-priced, not
      //      function-backed).
      //
      //   3. compareAtSavings: compare_at_price - item.price (MSRP to sale).
      //      Disjoint from the above because compare_at is marketing metadata,
      //      never reflected in cart.total_discounts or final_line_price.

      const lineSavings = (cart.items || []).reduce((sum, i) => {
        const orig  = i.original_line_price != null ? i.original_line_price : (i.line_price || 0);
        const final = i.final_line_price   != null ? i.final_line_price   : (i.line_price || 0);
        return sum + Math.max(0, orig - final);
      }, 0);

      const cartLevelExtra = Math.max(0, (cart.total_discounts || 0) - lineSavings);

      const compareAtPrices = window.cartCompareAtPrices || {};
      const compareAtSavings = (cart.items || []).reduce((sum, i) => {
        const cmp  = compareAtPrices[i.key] || i.variant?.compare_at_price || i.compare_at_price || 0;
        const base = i.price != null ? i.price : (i.original_price != null ? i.original_price : (i.final_price || 0));
        return cmp > base ? sum + (cmp - base) * i.quantity : sum;
      }, 0);

      const totalSavings = lineSavings + cartLevelExtra + compareAtSavings;

      // Non-credit savings row (member %, sale discounts, compare_at).
      // Order-summary rows all use the with-currency format ("$16.00 CAD")
      // per design spec; line-item prices keep the short format.
      if (savingsRow && savingsVal) {
        savingsRow.style.display = totalSavings > 0 ? '' : 'none';
        savingsVal.textContent   = `-${formatMoneyWithCurrency(totalSavings)}`;
      }

      // Fable credit row is never shown from the drawer — Subscribfy OTP
      // requirement means the apply flow lives at /checkout. Hide the row
      // unconditionally to stay consistent with the disabled apply path.
      if (creditSummRow) {
        creditSummRow.style.display = 'none';
      }

      // Subtotal = cart.total_price (after Shopify-scope discounts).
      if (subtotalVal) {
        subtotalVal.textContent = formatMoneyWithCurrency(cart.total_price);
      }

      // Shipping protection: 4.5% of ORIGINAL (compare-at) line prices,
      // not cart.total_price — see estimateInsuranceFee comment block.
      if (cfg.insuranceEnabled && cfg.insuranceVariantId && protRow && protVal) {
        const insuranceItem = cart.items?.find(i => i.handle === 'shipping-insurance');
        if (insuranceItem) {
          protRow.style.display = '';
          protVal.textContent   = formatMoneyWithCurrency(insuranceItem.final_price);
        } else {
          const fee = estimateInsuranceFee(cart);
          protRow.style.display = '';
          protVal.textContent   = formatMoneyWithCurrency(fee);
        }
      } else if (protRow) {
        protRow.style.display = 'none';
      }
    },

    _updateCheckoutBtn(cart) {
      if (!cart) cart = this.cart;
      if (!cart) return;

      const btn     = this.el.checkoutBtn();
      const altBtn  = this.el.checkoutAlt();
      const totalEl = this.el.checkoutTotal();

      if (!btn || !totalEl) return;

      // Empty cart — disable CHECKOUT and hide "checkout without protection"
      // link so users can't navigate to checkout with a $0 order (Shopify
      // rejects it anyway, but disabling prevents the frustrating flash).
      // "Empty" also covers the Fable+ membership-only state: when the
      // only line in the cart is the membership subscription variant
      // (auto-added when JOIN NOW is pre-selected), there are no real
      // products to check out with — Shopify would refuse and the $0.00
      // CHECKOUT total made it obvious. Filter the membership line out
      // of the count so the button stays disabled until the shopper adds
      // an actual product.
      const membershipVid = parseInt(cfg.membershipVariantId) || 0;
      const realItemCount = (cart.items || []).reduce(
        (n, i) => (i.variant_id === membershipVid ? n : n + 1),
        0
      );
      const isEmpty = !cart.item_count || cart.item_count === 0 || realItemCount === 0;
      btn.disabled = isEmpty;
      btn.classList.toggle('is-disabled', isEmpty);
      btn.setAttribute('aria-disabled', isEmpty ? 'true' : 'false');
      if (altBtn) {
        altBtn.style.display = isEmpty ? 'none' : '';
      }
      if (isEmpty) {
        totalEl.textContent = formatMoney(0);
        return;
      }

      const baseTotal = cart.total_price;

      if (cfg.insuranceEnabled && cfg.insuranceVariantId) {
        const insuranceItem = cart.items?.find(i => i.handle === 'shipping-insurance');
        const fee = insuranceItem
          ? insuranceItem.final_price
          : estimateInsuranceFee(cart);
        const total = baseTotal + (insuranceItem ? 0 : fee);
        totalEl.textContent = formatMoney(total);
        if (altBtn) altBtn.style.display = '';
      } else {
        totalEl.textContent = formatMoney(baseTotal);
        if (altBtn) altBtn.style.display = 'none';
      }
    },

    /* ----------------------------------------------------------------
       UPDATE CART COUNT IN HEADER
    ---------------------------------------------------------------- */
    _updateCartCount(cart) {
      const dots = qsa('.inCart');
      dots.forEach(d => {
        if (cart.item_count > 0) d.classList.remove('hidden');
        else d.classList.add('hidden');
      });
    },

    /* ----------------------------------------------------------------
       LOAD RECOMMENDATIONS — Section Rendering API
       ----
       Fetches `/recommendations/products?product_id=X&section_id=cart-rec-slider&intent=related&limit=10`.
       Shopify renders the `sections/cart-rec-slider.liquid` section on the
       server with `recommendations.products` populated — that section
       handles ALL filtering, swatch rendering (using native `value.swatch`)
       and per-variant data embedding. We just inject the returned HTML.

       Advantages vs. the old `.json` + build-in-JS path:
       - Swatches match PDP pixel-for-pixel (shared Liquid data source)
       - Metafield access in Liquid for Fable+ preview, future perks
       - Shopify CDN caches the section response per (product_id, section_id)
       - Single network request, no client-side DOM building
    ---------------------------------------------------------------- */
    async _loadRecs() {
      const recs  = this.el.recs();
      const track = this.el.recsTrack();
      if (!recs || !track) return;

      const cart = this.cart;
      if (!cart || !cart.item_count) {
        recs.style.display = 'none';
        return;
      }

      // Seed recs from the first real product in the cart — NOT the Fable+
      // membership line. Shopify's cart.add prepends new items, so without
      // this filter, joining Fable+ re-seeds /recommendations/products with
      // the membership product and the upsells silently swap. Picking the
      // first non-membership line keeps upsells anchored to what the shopper
      // actually added.
      const membershipVid = parseInt(cfg.membershipVariantId) || 0;
      const firstItem = cart.items.find(i => i.product_id && i.variant_id !== membershipVid);
      if (!firstItem) { recs.style.display = 'none'; return; }

      const pid = firstItem.product_id;

      // Skip refetch if recs for this product were loaded in last 60s and the DOM already has them
      const cached = this._recsCache;
      if (cached && cached.productId === pid && Date.now() - cached.time < 60000 && track.children.length) {
        recs.style.display = '';
        return;
      }

      try {
        const url = `/recommendations/products?product_id=${pid}` +
                    `&section_id=cart-rec-slider&intent=related&limit=10`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`recommendations HTTP ${res.status}`);
        const raw = await res.text();

        // Shopify wraps the section in a <div id="shopify-section-cart-rec-slider" class="shopify-section">…</div>.
        // Extract the inner HTML so only our card markup ends up in the track.
        const inner = this._extractSectionInner(raw);

        if (!inner.trim()) {
          recs.style.display = 'none';
          return;
        }

        // Filter out products already in the cart (the section can't know
        // this — Liquid has no access to the live cart at rendering time).
        const cartProductIds = new Set(cart.items.map(i => String(i.product_id)));
        track.innerHTML = inner;
        track.querySelectorAll('.cd-rec-card').forEach(card => {
          if (cartProductIds.has(card.dataset.productId)) card.remove();
        });

        // Sync finish-split initial selection state. Section rendering
        // occasionally strips `value.selected` from `option.selected_value`
        // when a product is pulled via `all_products[handle]` without a
        // variant URL context, so the Liquid-side `{% if v.selected %}`
        // check silently reports false. We bypass that by reading the
        // rendered variant label (which IS populated correctly) and
        // toggling `.is-selected` on the matching sub-picker's main swatch.
        this._syncFinishSplitSelection(track);

        if (!track.querySelector('.cd-rec-card')) {
          recs.style.display = 'none';
          return;
        }

        this._recsCache = { productId: pid, time: Date.now() };
        recs.style.display = '';
        this._initRecsSlider(track);
      } catch (err) {
        console.warn('[CartDrawer] recommendations error', err);
        recs.style.display = 'none';
      }
    },

    /**
     * Strips the `<div id="shopify-section-…">` wrapper Shopify adds around
     * every section-rendered response. Returns just the inner HTML of our
     * rec-slider section (the rec cards themselves).
     */
    _extractSectionInner(raw) {
      const doc = new DOMParser().parseFromString(raw, 'text/html');
      const section = doc.querySelector('[id^="shopify-section-cart-rec-slider"]') ||
                      doc.querySelector('.shopify-section');
      return section ? section.innerHTML : raw;
    },

    /**
     * Finish-split (Semi-Matte / Glossy) initial selection patch.
     *
     * Problem: the Liquid render in snippets/cart-rec-option-single.liquid
     * tries to set `.is-selected` on the main SEMI-MATTE swatch based on
     * `semi_matte_display.selected` / `option.selected_value.name` — but
     * in Section Rendering API responses these values are unreliable for
     * products fetched via `all_products[handle]` (test injection) or
     * when variant context is stripped. The side effect is that neither
     * sub-picker gets the coral ring on load, even though the dropdown
     * items render correctly (because `{% if value.selected %}` inside
     * a `{% for %}` loop IS evaluated live per iteration and works).
     *
     * Fix: after we inject the section HTML, read the rendered variant
     * label that Shopify populates correctly (e.g. "Speckled White" or
     * "Pearl White") and toggle `.is-selected` on the matching sub-picker
     * main swatch. Same contract the click handler uses, so no visual
     * drift between load state and post-click state.
     */
    _syncFinishSplitSelection(track) {
      track.querySelectorAll('.cd-rec-card__option--finish-split').forEach(optionBlock => {
        const card = optionBlock.closest('.cd-rec-card');
        if (!card) return;

        // Variant label is the full variant title — for finish-split products
        // (single-option Colour) it's just the colour name. When the selected
        // colour is "Pearl White", Glossy is the active finish.
        const variantLabel = card.querySelector('.cd-rec-card__variant')?.textContent?.trim() || '';
        const isGlossy = variantLabel === 'Pearl White';

        const semiBtn  = optionBlock.querySelector('.cd-swatch-btn[data-finish-group="semi-matte"]');
        const glossyBtn = optionBlock.querySelector('.cd-swatch-btn[data-finish-group="glossy"]');
        if (semiBtn)   semiBtn.classList.toggle('is-selected', !isGlossy);
        if (glossyBtn) glossyBtn.classList.toggle('is-selected', isGlossy);
      });
    },

    /**
     * Reads the variants JSON blob Liquid emits inside each rec card.
     * Returned shape matches the legacy `/recommendations/products.json`
     * variant objects, so existing variant-matching code keeps working.
     */
    _readCardVariants(card) {
      if (!card) return [];
      if (card._variantsCache) return card._variantsCache;
      const node = card.querySelector('.cd-rec-card__variants-data');
      if (!node) return [];
      try {
        card._variantsCache = JSON.parse(node.textContent);
        return card._variantsCache;
      } catch (err) {
        console.warn('[CartDrawer] failed to parse variants data', err);
        return [];
      }
    },

    /**
     * Builds the four preorder line-item properties for a rec-card add-to-cart,
     * reading the variant's preorder_status/restock_date/batch_id from the
     * `.cd-rec-card__variants-data` JSON blob snippets/cart-rec-card.liquid
     * renders server-side. Same source and same gating as the theme.js
     * quick-add reference build (properties only when preorder_status is set).
     */
    _recPreorderProperties(card, variantId) {
      const variant = this._readCardVariants(card).find(v => String(v.id) === String(variantId));
      if (!variant || !variant.preorder_status) return {};
      const properties = {
        'Ship Date': variant.preorder_status,
        '_preorder': 'true',
      };
      if (variant.restock_date) properties['_restockDate'] = variant.restock_date;
      if (variant.batch_id) properties['Product Batch ID'] = variant.batch_id;
      return properties;
    },

    _initRecsSlider(track) {
      const prev = this.el.recsPrev();
      const next = this.el.recsNext();
      if (!prev || !next) return;

      const cards = [...track.querySelectorAll('.cd-rec-card')];
      if (!cards.length) return;

      let idx = 0;
      cards[0].classList.add('is-active');

      const show = (i) => {
        idx = Math.max(0, Math.min(cards.length - 1, i));
        cards.forEach((c, j) => c.classList.toggle('is-active', j === idx));
        prev.style.display = idx > 0 ? '' : 'none';
        next.style.display = idx < cards.length - 1 ? '' : 'none';
        // Close any open dropdown when changing card
        track.querySelectorAll('.cd-swatch-dropdown.is-open').forEach(d => d.classList.remove('is-open'));
      };

      show(0);

      prev.onclick = () => show(idx - 1);
      next.onclick = () => show(idx + 1);

      // --- Gesture navigation: touchpad horizontal scroll (wheel) + touch swipe.
      // Kept off the track element to avoid re-stacking listeners on rebuild —
      // re-binding each _loadRecs replaces `.onwheel` / `.ontouch*` properties
      // cleanly instead of accumulating addEventListener handlers.
      const SWIPE_PX = 60;
      let wheelCooldown = 0;
      track.onwheel = (e) => {
        const ax = Math.abs(e.deltaX);
        const ay = Math.abs(e.deltaY);
        // Only hijack when the gesture is clearly horizontal — preserves vertical
        // page/drawer scroll on touchpads that emit small deltaX noise.
        if (ax < 8 || ax <= ay) return;
        e.preventDefault();
        const now = Date.now();
        if (now < wheelCooldown) return;
        wheelCooldown = now + 350;
        show(idx + (e.deltaX > 0 ? 1 : -1));
      };

      let tStartX = 0, tStartY = 0, tLocked = null;
      track.ontouchstart = (e) => {
        const t = e.changedTouches[0];
        tStartX = t.clientX;
        tStartY = t.clientY;
        tLocked = null;
      };
      track.ontouchmove = (e) => {
        if (tLocked === 'v') return;
        const t  = e.changedTouches[0];
        const dx = t.clientX - tStartX;
        const dy = t.clientY - tStartY;
        if (tLocked === null) {
          if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
          tLocked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        }
        if (tLocked === 'h') e.preventDefault();
      };
      track.ontouchend = (e) => {
        if (tLocked !== 'h') return;
        const t  = e.changedTouches[0];
        const dx = t.clientX - tStartX;
        if (Math.abs(dx) >= SWIPE_PX) show(idx + (dx < 0 ? 1 : -1));
      };
    },

    _bindRecsActions() {
      // Recs listeners are attached once on drawer root + document in
      // _bindItemActionsOnce / _bindRecsActionsOnce. Noop here to avoid
      // stacking handlers across re-loads.
    },

    _bindRecsActionsOnce() {
      const root = this.drawer;

      root.addEventListener('click', async (e) => {
        const track = e.target.closest('#cdRecsTrack');
        if (!track) return;

        // Add to cart
        const addBtn = e.target.closest('.cd-rec-card__add-btn[data-variant-id]');
        if (addBtn) {
          e.preventDefault();
          await this._addRecommendation(addBtn);
          return;
        }

        // Swatch toggle (open/close dropdown)
        const toggleBtn = e.target.closest('.cd-swatch-toggle');
        if (toggleBtn) {
          e.preventDefault();
          e.stopPropagation();
          const pid      = toggleBtn.dataset.productId;
          const optIdx   = toggleBtn.dataset.optionIdx;
          const dropdown = track.querySelector(`.cd-swatch-dropdown[data-product-id="${pid}"][data-option-idx="${optIdx}"]`);
          if (dropdown) {
            track.querySelectorAll('.cd-swatch-dropdown.is-open').forEach(d => {
              if (d !== dropdown) d.classList.remove('is-open');
            });
            dropdown.classList.toggle('is-open');
          }
          return;
        }

        // Swatch selection (inline btn or dropdown item)
        const swatchBtn = e.target.closest('.cd-swatch-btn, .cd-size-btn, .cd-swatch-dropdown__item');
        if (swatchBtn) {
          e.preventDefault();
          const pid      = swatchBtn.dataset.productId;
          const optIdx   = parseInt(swatchBtn.dataset.optionIdx);
          const newVal   = swatchBtn.dataset.value;

          // If this is a MAIN swatch (inline, not a dropdown item) that has
          // a sibling dropdown arrow, treat the tap as "open dropdown"
          // instead of a variant change. Rationale: a main swatch represents
          // the current selection, so clicking it "to select it" is a no-op
          // for the cart, but shoppers naturally tap the swatch itself to
          // see alternatives. Keeping the arrow as the only affordance makes
          // the tap target too small on mobile. Dropdown-item clicks still
          // change the variant normally.
          const isDropdownItem = swatchBtn.classList.contains('cd-swatch-dropdown__item');
          const swatchGroup = swatchBtn.closest('.cd-rec-card__swatch-group');
          const hasSiblingToggle = !!swatchGroup?.querySelector('.cd-swatch-toggle');
          if (!isDropdownItem && hasSiblingToggle) {
            e.stopPropagation();
            const optCol = swatchBtn.closest('.cd-rec-card__opt-col');
            const dropdown = optCol?.querySelector(`.cd-swatch-dropdown[data-product-id="${pid}"][data-option-idx="${optIdx}"]`);
            if (dropdown) {
              track.querySelectorAll('.cd-swatch-dropdown.is-open').forEach(d => {
                if (d !== dropdown) d.classList.remove('is-open');
              });
              dropdown.classList.toggle('is-open');
            }
            return;
          }

          if (pid && newVal !== undefined) {
            this._selectVariantOption(track, pid, optIdx, newVal);
            const dropdown = swatchBtn.closest('.cd-swatch-dropdown');
            if (dropdown) dropdown.classList.remove('is-open');
          }
        }
      });

      // Close dropdowns on outside click (bound once)
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.cd-rec-card__swatch-group, .cd-swatch-dropdown, .cd-swatch-toggle')) {
          const track = qs('#cdRecsTrack');
          if (track) track.querySelectorAll('.cd-swatch-dropdown.is-open').forEach(d => d.classList.remove('is-open'));
        }
      });
    },

    _selectVariantOption(track, productId, optionIdx, newValue) {
      const card = track.querySelector(`[data-product-id="${productId}"]`);
      if (!card) return;

      const variants = this._readCardVariants(card);
      if (!variants.length) return;

      // Current selection from the card's is-selected state. Includes
      // swatch btns, size btns, AND dropdown items so we always have a
      // fresh read of every option's current value.
      const firstVariantOpts = variants[0]?.options || [];
      const currentOptions = firstVariantOpts.map(() => null);
      card.querySelectorAll('.is-selected[data-option-idx]').forEach(btn => {
        const idx = parseInt(btn.dataset.optionIdx);
        if (!isNaN(idx) && btn.dataset.value) currentOptions[idx] = btn.dataset.value;
      });
      currentOptions[optionIdx] = newValue;

      const matchingVariant = variants.find(v =>
        (v.options || []).every((opt, i) => opt === currentOptions[i])
      ) || variants.find(v => v.options?.[optionIdx] === newValue);

      if (!matchingVariant) return;

      // Toggle `.is-selected` on every button that carries this optionIdx
      // (twin-mode has two visible swatches as siblings → both re-toggle;
      // dropdown items also re-toggle to keep the dropdown state clean).
      card.querySelectorAll(`[data-option-idx="${optionIdx}"]`).forEach(btn => {
        btn.classList.toggle('is-selected', btn.dataset.value === newValue);
      });

      // Single-mode main swatch — only ONE visible swatch represents the
      // currently selected value. Rewrite its value + background. Source
      // the new background from the matching dropdown item rendered by
      // Liquid, so swatch textures from native `value.swatch.image` stay
      // pixel-identical to PDP (no JS-side colour guessing).
      //
      // Finish-split (Pearl White) nuance: the Semi-Matte/Glossy card has
      // TWO visible main swatches for the same option_idx. The Glossy
      // swatch is fixed at Pearl White and never rewrites. The Semi-Matte
      // swatch shows its last non-Pearl-White preview and also skips
      // rewriting when the shopper picks Pearl White — `is-selected`
      // toggling is already handled by the generic loop above so the
      // visual coral ring jumps between the two sub-pickers correctly.
      const isFinishSplitCard = !!card.querySelector('.cd-rec-card__option--finish-split');
      const skipMainRewrite = isFinishSplitCard && newValue === 'Pearl White';
      const singleMain = skipMainRewrite ? null : card.querySelector(
        `.cd-rec-card__option:not(.cd-rec-card__option--twin) .cd-swatch-btn[data-option-idx="${optionIdx}"]:not([data-finish-group="glossy"]),` +
        `.cd-rec-card__option:not(.cd-rec-card__option--twin) .cd-size-btn[data-option-idx="${optionIdx}"]:not([data-finish-group="glossy"])`
      );
      if (singleMain) {
        singleMain.dataset.value = newValue;
        singleMain.setAttribute('aria-label', newValue);
        singleMain.classList.add('is-selected');
        if (singleMain.classList.contains('cd-size-btn')) {
          // Abbreviate 3+ char sizes to first letter to match the Liquid
          // render in cart-rec-swatch-btn.liquid / cart-rec-option-single.liquid
          // (threshold is "length > 2"). Keeps 1–2 char labels like S / M / L / XL
          // intact, turns "Tall" / "Short" / "Small" / "Medium" into T / S / S / M.
          singleMain.textContent = newValue.length > 2 ? newValue.charAt(0) : newValue;
        } else {
          // Find the matching dropdown item and copy its swatch circle's
          // inline background — that circle was rendered server-side with
          // the authoritative `value.swatch.image` / `.color` data.
          const dropdownItem = card.querySelector(
            `.cd-swatch-dropdown__item[data-option-idx="${optionIdx}"][data-value="${CSS.escape(newValue)}"]`
          );
          const circle = dropdownItem?.querySelector('.cd-swatch-dropdown__circle');
          if (circle && circle.style.background) {
            singleMain.style.background = circle.style.background;
          }
          singleMain.setAttribute('title', newValue);
        }
        if (matchingVariant.available === false) singleMain.dataset.soldOut = 'true';
        else delete singleMain.dataset.soldOut;
      }

      card.dataset.activeVariant = matchingVariant.id;

      const addBtn = card.querySelector('.cd-rec-card__add-btn');
      if (addBtn) {
        addBtn.dataset.variantId = matchingVariant.id;
        addBtn.disabled = !matchingVariant.available;
        addBtn.classList.toggle('is-sold-out', !matchingVariant.available);
        addBtn.textContent = matchingVariant.available ? 'ADD +' : 'Sold out';
      }

      const priceEl = card.querySelector(`#cdRecPrice_${productId}`);
      if (priceEl) this._renderRecPrice(priceEl, matchingVariant);

      // Update image — variant.featured_image may be null, fall back to card.
      const imgEl = card.querySelector(`#cdRecImg_${productId}`);
      if (imgEl && matchingVariant.featured_image) {
        const src = matchingVariant.featured_image
          .replace('http:', 'https:')
          .replace(/(\.[a-z]+(\?.*)?)$/, '_200x200$1');
        imgEl.src = src;
      }

      const variantEl = card.querySelector(`#cdRecVariant_${productId}`);
      if (variantEl) {
        const title = matchingVariant.title && matchingVariant.title !== 'Default Title'
          ? matchingVariant.title : '';
        variantEl.textContent = title;
        variantEl.hidden = !title;
      }
    },

    /**
     * Re-renders the rec-card price block for a newly picked variant. The block
     * follows the Fable Discounts card contract (snippets/cart-rec-card.liquid):
     * a fresh node with the variant's data-fd-* attributes replaces the old one,
     * and fable-discounts-preview.js prices it from the discount preview when the
     * node is added. Membership changes re-price via the preview's cart re-read.
     */
    _renderRecPrice(priceEl, variant) {
      const price     = variant?.price || 0;
      const compareAt = variant?.compare_at_price > price ? variant.compare_at_price : 0;
      const el = document.createElement('div');
      el.className = 'cd-rec-card__prices fd-price' + (compareAt ? ' fd-price--on-sale' : '');
      el.id = priceEl.id;
      ['data-fd-product', 'data-fd-tags', 'data-fd-collections'].forEach(a => {
        el.setAttribute(a, priceEl.getAttribute(a) || '');
      });
      el.setAttribute('data-fd-variant', variant?.id || '');
      el.setAttribute('data-fd-price', price);
      el.setAttribute('data-fd-compare', compareAt || '');
      el.innerHTML = `
          <s class="cd-rec-card__price--compare fd-price__was">${compareAt ? formatMoney(compareAt) : ''}</s>
          <span class="cd-rec-card__price fd-price__now">${formatMoney(price)}</span>`;
      priceEl.replaceWith(el);
    },

    /* ----------------------------------------------------------------
       UTILITY
    ---------------------------------------------------------------- */
    _esc(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
  };

  /* ============================================================
     CART INDEX — rebuild item line numbers after each update
     ============================================================ */
  function indexCartLines(cart) {
    if (!cart?.items) return cart;
    cart.items = cart.items.map((item, i) => {
      item.line_idx = i + 1;
      return item;
    });
    return cart;
  }

  /* Patch CartAPI.get to always index lines */
  const _origGet = CartAPI.get.bind(CartAPI);
  CartAPI.get = async function () {
    const cart = await _origGet();
    return indexCartLines(cart);
  };
  const _origChange = CartAPI.change.bind(CartAPI);
  CartAPI.change = async function (id, qty, line) {
    const cart = await _origChange(id, qty, line);
    return indexCartLines(cart);
  };

  /* ============================================================
     BOOT
     ============================================================ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CartDrawer.init());
  } else {
    CartDrawer.init();
  }

  /* Expose for external use */
  window.CartDrawer = CartDrawer;

})();
